import { Auth, graphqlOperation, } from 'aws-amplify';
// included via aws-amplify
// eslint-disable-next-line import/no-extraneous-dependencies
import { GRAPHQL_AUTH_MODE, } from '@aws-amplify/api-graphql';
import { cast, destroy, flow, getSnapshot, types, } from 'mobx-state-tree';
import { cloneDeep, difference, isEqual, omit, throttle, uniqBy, } from 'lodash-es';
import { v4 } from 'uuid';
import { getTimestamp } from './logger';
// import airbrake from './airbrake';
import { publishToRepository as publishToRepositoryMutation, setAccessLevel as setAccessLevelMutation, updateOrganizationPaymentCreds, updateQuizResults, createCognitoCustomer, addUserToOrganization as addUserToOrganizationGQL, resetPassword as resetPasswordGQL, executeEngagementFlow, createLead, enrollCustomers as enrollCustomersGQL, } from './aws-temp/graphql/mutations';
import { getCustomersWithProgress as getCustomersWithProgressGQL, programProgressByProgramIDCustomerID, } from './aws-temp/graphql/queries';
import schema, { FULL_ITEM_FROM_SEARCH_MODELS, isOrganizationScoped, JsonType, resolvableReferenceType, resolveKey, rootTypes, } from './Schema';
import { Asset, getAssetTypeFromFile, processAssetGQL, syncDriveFilesGQL, } from './AssetModel';
import { buildItemProgressSearchInput } from './itemProgressSearchInput';
import { 
// Conversation,
getAISessionGQL, getAllAISessionsGQL, runQueryGQL,
// IConversation,
// CustomerContext,
 } from './ConversationModel';
import { Certificate, Certification, Course, CourseSection, Review, FAQ, Prerequisite, Instructor, CourseProgress, trackCourseItemProgressGQL, startCourseGQL, scormCommitGQL, getSignedCertificateUrl, previewCertificate, } from './CourseModel';
import { Customer, customerInfoGQL, getLearningBoardGQL, listQuestionairesByEmailGQL, LearningBoard, LearningBoardSection, Payment, refreshCustomerStatsGql, setCustomerAttributesGQL, invokeAgentGQL, getAgentSessionGQL, } from './CustomerModel';
import { Product, } from './ProductModel';
import { EcommerceCategory, } from './EcommerceCategoryModel';
import { WebPage, } from './WebPageModel';
import { ShopifyConfig, } from './ShopifyConfigModel';
import { Document, DocumentProgress, startDocumentGQL, } from './DocumentModel';
import { Hub, HubCategory } from './HubModel';
import { Lead, } from './LeadModel';
import { Profile, } from './ProfileModel';
import { navStore } from './NavStore';
import { Microcast, MicrocastProgress, startMicrocastGQL, trackMicrocastProgressGQL, } from './MicrocastModel';
import { Kicker, } from './KickerModel';
import { Topic, Post, } from './DiscussionModel';
import { Question, Questionnaire, } from './QuestionnaireModel';
import { EngagementFlow, } from './EngagementFlowModel';
import { EmailTemplate } from './EmailTemplateModel';
import { AccessGroupDefinition, CrawlConfig, ClientAi, getGoogleAccessTokenGQL, getGoogleSyncConfigGQL, Organization, syncOrganizationToCognitoGQL, User, UserOrganization, checkStatusGQL, } from './OrganizationModel';
import { Member, AIConfiguration, LearningBoardConfiguration, startCheckoutGQL, verifyCheckoutGQL, swapMembershipGQL, } from './PortalModel';
import { Item, } from './ItemModel';
import { Meeting, Registration, createPortalMeetingGQL, endMeetingGQL, getMeetingAttendeeInfoGQL, getMeetingRegistrationForCustomerGQL, joinMeetingGQL, startMeetingGQL, registerForMeetingGQL, startMeetingRecordingGQL, stopMeetingRecordingGQL, sendMeetingInvitesGQL, } from './MeetingModel';
import { Program, ProgramProgress, ProgramSection, ProgramItem, startProgramGQL, trackProgramProgressGQL, } from './ProgramModel';
import { putObject } from './S3Client';
import { CachedListModel, Category, ItemProgress, Library, LibraryShare, Repository, Membership, Portal, PortalTemplate, } from './PageSetModel';
import { ChimeAttendeeType } from './ChimeTypes';
/**
 * This is being set very high because previously
 * items were cached forever and we dont always handle
 * getting items correctly.
 */
const DEFAULT_MAX_AGE = 60 * 60 * 24; // 24 hours
/**
 * Pass as `maxAge` to `load` / `getOrLoad` to always refetch from the server when the item
 * is clean (`changeCount === 0`). Values `<= 0` skip the TTL short-circuit (see `load`).
 */
export const STORE_LOAD_FRESH = 0;
let GQLAPI;
export const setGQLAPI = (newValue) => {
    // console.log('Setting GQLAPI', newValue);
    GQLAPI = newValue;
};
export const getGQLAPI = () => GQLAPI;
const membershipOperationType = (operation) => (operation.startsWith('get') ? 'query' : 'mutation');
const membershipInputType = {
    getProductGuideMembershipEditor: 'ProductGuideMembershipEditorInput!',
    saveProductGuideMembership: 'ProductGuideMembershipSaveInput!',
    getCategoryGuideMembershipEditor: 'CategoryGuideMembershipEditorInput!',
    previewCategoryGuideMembership: 'CategoryGuideMembershipPreviewInput!',
    previewGuideCategoryMembership: 'CategoryGuideMembershipPreviewInput!',
    confirmCategoryGuideMembership: 'CategoryGuideMembershipConfirmInput!',
};
/** Established old-admin AppSync seam for platform-owned Guide membership operations. */
export async function runGuideMembershipGQL(operation, input) {
    const document = `${membershipOperationType(operation)} ${operation}($input: ${membershipInputType[operation]}) {
    ${operation}(input: $input) {
      code message retryable updatedAt membershipIds projectionStatus payload
    }
  }`;
    const response = await getGQLAPI().graphql(graphqlOperation(document, { input }));
    const envelope = response?.data?.[operation];
    if (!envelope)
        throw new Error('Guide membership returned no result');
    if (envelope.code !== 'OK') {
        const error = new Error(envelope.message || 'Guide membership operation failed');
        error.code = envelope.code;
        error.retryable = envelope.retryable;
        throw error;
    }
    if (typeof envelope.payload === 'string')
        return JSON.parse(envelope.payload);
    return envelope.payload;
}
const ItemProgressLink = types.model('ItemProgressLink', {
    key: types.identifier, // this is the key of the course/program/etc
    progress: types.maybeNull(types.reference(types.union(CourseProgress, DocumentProgress, MicrocastProgress, ProgramProgress, Registration))),
})
    .actions((self) => ({
    setProgress(newProgress) {
        self.progress = newProgress;
    },
}));
rootTypes.set('accessGroupDefinition', AccessGroupDefinition);
// rootTypes.set('AccessGroup', AccessGroupType);
rootTypes.set('aiConfiguration', AIConfiguration);
rootTypes.set('learningBoardConfiguration', LearningBoardConfiguration);
rootTypes.set('asset', Asset);
rootTypes.set('category', Category); // not sure we need/want this here?
rootTypes.set('certificate', Certificate);
rootTypes.set('certification', Certification);
rootTypes.set('clientAi', ClientAi);
rootTypes.set('course', Course);
rootTypes.set('courseSection', CourseSection);
rootTypes.set('courseProgress', CourseProgress);
rootTypes.set('crawlConfig', CrawlConfig);
rootTypes.set('customer', Customer);
rootTypes.set('document', Document);
rootTypes.set('documentProgress', DocumentProgress);
rootTypes.set('faq', FAQ);
rootTypes.set('hub', Hub);
rootTypes.set('hubCategory', HubCategory);
rootTypes.set('instructor', Instructor);
rootTypes.set('lead', Lead);
rootTypes.set('profile', Profile);
rootTypes.set('learningBoard', LearningBoard);
rootTypes.set('learningBoardSection', LearningBoardSection);
rootTypes.set('library', Library);
rootTypes.set('libraryShare', LibraryShare);
rootTypes.set('meeting', Meeting);
rootTypes.set('meetingRegistration', Registration);
rootTypes.set('member', Member);
rootTypes.set('membership', Membership); // not sure we need/want this here?
rootTypes.set('organization', Organization);
rootTypes.set('payment', Payment);
rootTypes.set('portal', Portal);
rootTypes.set('product', Product);
rootTypes.set('ecommerceCategory', EcommerceCategory);
rootTypes.set('webPage', WebPage);
rootTypes.set('portalTemplate', PortalTemplate);
rootTypes.set('prerequisite', Prerequisite);
rootTypes.set('program', Program);
rootTypes.set('programItem', ProgramItem);
rootTypes.set('programProgress', ProgramProgress);
rootTypes.set('programSection', ProgramSection);
rootTypes.set('item', Item);
rootTypes.set('itemProgress', ItemProgress);
rootTypes.set('question', Question);
rootTypes.set('questionnaire', Questionnaire);
rootTypes.set('repository', Repository);
rootTypes.set('review', Review);
rootTypes.set('shopifyConfig', ShopifyConfig);
rootTypes.set('user', User);
rootTypes.set('userOrganization', UserOrganization);
rootTypes.set('microcast', Microcast);
rootTypes.set('microcastProgress', MicrocastProgress);
rootTypes.set('kicker', Kicker);
rootTypes.set('discussionTopic', Topic);
rootTypes.set('discussionPost', Post);
rootTypes.set('engagementFlow', EngagementFlow);
rootTypes.set('emailTemplate', EmailTemplate);
export const getListKey = (organizationId, modelName, filter = {}, sort = {}, crossOrganization = false) => (`${organizationId}/${modelName}/${JSON.stringify(filter)}/${JSON.stringify(sort)}${crossOrganization ? '/crossOrganization' : ''}`);
const splitKey = (key, currentOrganizationID) => {
    if (!key) {
        return { id: null, organizationID: currentOrganizationID };
    }
    const keyParts = key.split('~');
    if (keyParts.length === 1) {
        return {
            id: key,
            organizationID: currentOrganizationID,
        };
    }
    return {
        organizationID: keyParts[0],
        id: keyParts[1],
    };
};
const chunkArray = (arr, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunks.push(arr.slice(i, chunkSize + i));
    }
    return chunks;
};
/**
 * WebPage id is stored as `crawlID-${encodeURIComponent(url)}`.
 * When the key comes from a URL path (e.g. React Router useParams), the path is decoded,
 * so we may receive `crawlID-${url}` with slashes and colons. Re-encode for API lookup.
 */
const normalizeWebPageId = (id) => {
    if (!id || id.includes('%3A'))
        return id; // already encoded or empty
    const urlMatch = id.match(/(https?:\/\/.*)$/);
    if (!urlMatch)
        return id;
    const urlPart = urlMatch[1];
    const crawlID = id.slice(0, -(urlPart.length + 1));
    return `${crawlID}-${encodeURIComponent(urlPart)}`;
};
const getIDsFromKey = (modelName, key, currentOrganizationID) => {
    if (!key) {
        return {
            id: null,
            key: null,
            organizationID: undefined,
        };
    }
    if (isOrganizationScoped(modelName)) {
        const keyParts = key.split('~');
        if (keyParts.length === 1) {
            const id = modelName === 'webPage' ? normalizeWebPageId(key) : key;
            return {
                organizationID: currentOrganizationID,
                id,
                key: `${currentOrganizationID}~${id}`,
            };
        }
        const rawId = keyParts[1];
        const id = modelName === 'webPage' ? normalizeWebPageId(rawId) : rawId;
        return {
            organizationID: keyParts[0],
            id,
            key: `${keyParts[0]}~${id}`,
        };
    }
    // not org scoped - key is always just the id
    return {
        id: key,
        key,
        organizationID: undefined,
    };
};
const getIDsFromRemoteItem = (modelName, remoteItem) => {
    if (modelName === 'itemProgress') {
        return {
            id: remoteItem.id,
            key: `itemProgress~${remoteItem.id}`,
        };
    }
    if (isOrganizationScoped(modelName)) {
        return {
            id: remoteItem.id,
            organizationID: remoteItem.organizationID,
            key: `${remoteItem.organizationID}~${remoteItem.id}`,
        };
    }
    // not org scoped - key is always just the id
    return {
        id: remoteItem.id,
        key: remoteItem.id,
    };
};
/**
 * Returns the straigh GQL result.
 * TODO: show throw error if not found
 * TODO: might want to support eagerly fetching references in some cases
 *
 * @param modelName
 * @param id
 * @returns
 */
export async function loadItem(modelName, key, currentOrganizationID, isPortalApi = false) {
    // console.log(`GQL loadItem ${modelName}:${key}, currentOrg: ${currentOrganizationID}`);
    const { id, organizationID } = getIDsFromKey(modelName, key, currentOrganizationID);
    const variables = {
        id,
    };
    const filterByOrg = isOrganizationScoped(modelName);
    if (filterByOrg && !organizationID) {
        throw Error(`Missing organization when loading: ${modelName}`);
    }
    if (filterByOrg) {
        variables.organizationID = organizationID;
    }
    const query = schema.getItemGQL(modelName, isPortalApi);
    // console.log('query', { query, variables });
    const results = await getGQLAPI().graphql({
        query,
        variables,
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    });
    const { data } = results;
    const itemName = `get${schema.getTypeName(modelName)}`;
    // console.log('results', results);
    if (!data[itemName]) {
        console.log('No data!', data);
        return null;
    }
    // have the data, now create the item or merge data on existing item
    return data[itemName];
}
function normalizeModelKeyForSearchPath(modelName) {
    return (modelName === 'class' ? 'course' : modelName).toLowerCase();
}
/**
 * Per-model search path override for list loads. Only `pgvector` and `itemProgressSearch`
 * are honored; any other value behaves like no override.
 * Keys: PageSet `modelName` style, lowercase (`class` → `course`).
 */
const FORCE_SEARCH_PATH_BY_MODEL = {
    product: 'pgvector',
    itemprogress: 'itemProgressSearch',
};
function getForcedSearchPath(modelName) {
    const raw = FORCE_SEARCH_PATH_BY_MODEL[normalizeModelKeyForSearchPath(modelName)];
    if (raw === 'pgvector' || raw === 'itemProgressSearch')
        return raw;
    return undefined;
}
function getListPageSize(modelName) {
    const modelKey = normalizeModelKeyForSearchPath(modelName);
    return modelKey === 'product' || modelKey === 'course' ? 20 : 50;
}
/**
 * `buildListRequestPgVector` only forwards a fixed set of filter fields (organizationID,
 * libraryIds, text, searchInstructors, includeDoNotIndex, searchableOnly, assetTypes).
 * Any other filter field on a `getCachedList` call is silently dropped, returning an
 * unfiltered list. For relationship filters that Dynamo can answer directly (e.g. Member
 * by customerID via the getMemberByCustomerID GSI), fall back to the `list` resolver so
 * the filter is actually applied.
 *
 * Keys: PageSet modelName (lowercase, `class` already normalized to `course`).
 */
const SEARCH_INCOMPATIBLE_FILTER_FIELDS_BY_MODEL = {
    member: ['customerID'],
    customer: ['hubID'],
    questionnaire: ['email'],
    /** Dynamo list applies tags/endAt; pgvector search does not model them. */
    kicker: ['tags', 'endAt'],
};
/** Detect a non-empty `matchPhrasePrefix` text query anywhere in a filter shape. */
function filterContainsTextSearch(filter) {
    if (!filter || typeof filter !== 'object')
        return false;
    for (const [key, value] of Object.entries(filter)) {
        if (key === 'matchPhrasePrefix' && typeof value === 'string' && value.trim())
            return true;
        if (Array.isArray(value)) {
            if (value.some((sub) => filterContainsTextSearch(sub)))
                return true;
        }
        else if (value && typeof value === 'object') {
            if (filterContainsTextSearch(value))
                return true;
        }
    }
    return false;
}
/** Recursively walk a Model<X>FilterInput / SearchableXFilterInput shape to detect a field. */
function filterContainsField(filter, fieldName) {
    if (!filter || typeof filter !== 'object')
        return false;
    const f = filter;
    if (Object.prototype.hasOwnProperty.call(f, fieldName))
        return true;
    for (const k of ['and', 'or', 'not']) {
        const v = f[k];
        if (Array.isArray(v)) {
            if (v.some((sub) => filterContainsField(sub, fieldName)))
                return true;
        }
        else if (v && typeof v === 'object') {
            if (filterContainsField(v, fieldName))
                return true;
        }
    }
    return false;
}
/**
 * Filter fields that `buildListRequestPgVector` actually models. Anything outside this
 * set (and the per-model extras) is silently dropped from the search request, so the
 * server returns an unfiltered list. `matchPhrasePrefix` operators on any field are
 * picked up by the text-extraction pass and aren't considered dropped.
 */
const SEARCH_PATH_RECOGNIZED_FILTER_FIELDS = new Set([
    'organizationID', 'organizationId',
    'libraryIds',
    'combineOrganizationWithLibraries',
    'libraries', // { matchPhrase } extracted into libraryIds
    'text',
    'searchableOnly',
    'includeDoNotIndex',
    'searchInstructors',
    'productTypes',
    'productOptions',
    'productType',
    'topicID',
    'status',
]);
function getModelExtraRecognizedFilterFields(modelName) {
    if (normalizeModelKeyForSearchPath(modelName) === 'asset')
        return ['type'];
    return [];
}
/** Read `{ eq: value }` from a Model*FilterInput field for KB search forwarding. */
function extractEqFilterField(filter, fieldName) {
    const raw = filter[fieldName];
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'eq' in raw) {
        const v = raw.eq;
        if (v != null && String(v).trim())
            return String(v).trim();
    }
    return undefined;
}
/** Walk filter, returning `${field}.${op}` pairs the search path will silently drop. */
function collectDroppedFilterFieldOps(filter, recognized, out = new Set()) {
    if (!filter || typeof filter !== 'object' || Array.isArray(filter))
        return out;
    for (const [k, v] of Object.entries(filter)) {
        if (k === 'and' || k === 'or') {
            if (Array.isArray(v)) {
                for (const sub of v)
                    collectDroppedFilterFieldOps(sub, recognized, out);
            }
            continue;
        }
        if (k === 'not') {
            if (v && typeof v === 'object' && !Array.isArray(v)) {
                collectDroppedFilterFieldOps(v, recognized, out);
            }
            continue;
        }
        if (recognized.has(k))
            continue;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            const ops = Object.keys(v);
            // matchPhrasePrefix on any field is consumed by text-extraction (see buildListRequestPgVector).
            for (const op of ops) {
                if (op === 'matchPhrasePrefix')
                    continue;
                out.add(`${k}.${op}`);
            }
        }
        else {
            out.add(k);
        }
    }
    return out;
}
/** Per-(model, field.op) dedupe so dev console isn't spammed across renders. */
const droppedFilterWarned = new Set();
function warnDroppedFilterFields(modelName, filter) {
    const recognized = new Set([
        ...SEARCH_PATH_RECOGNIZED_FILTER_FIELDS,
        ...getModelExtraRecognizedFilterFields(modelName),
    ]);
    const dropped = collectDroppedFilterFieldOps(filter, recognized);
    if (dropped.size === 0)
        return;
    const newDropped = [];
    for (const d of dropped) {
        const key = `${modelName}:${d}`;
        if (droppedFilterWarned.has(key))
            continue;
        droppedFilterWarned.add(key);
        newDropped.push(d);
    }
    if (newDropped.length === 0)
        return;
    console.warn(`[loadList] ${modelName}: search path is dropping unmodeled filter field(s): ${newDropped.join(', ')}. `
        + 'Result set will not honor these filters. Register the field in '
        + 'SEARCH_INCOMPATIBLE_FILTER_FIELDS_BY_MODEL (RootModel.ts) to route to the Dynamo '
        + 'list resolver, or extend buildListRequestPgVector forwarding.');
}
/**
 * Returns all matching items of the specified type.
 * Single exact match by slug or id (for URL resolution: /course/foo, /document/bar).
 * @param organizationId Organization ID (required for pgvector findBySlug)
 * @param modelName
 * @param slug Slug or id to look up (e.g. from URL path)
 * @param librariesToSearch Library IDs for portal filtering
 * @param isPortalApi
 * @returns List of matching items (0 or 1).
 */
export async function loadBySlug(organizationId, modelName, slug, librariesToSearch, isPortalApi = false) {
    if (!slug?.length) {
        return null;
    }
    // If it has ~ then that's org~id format; extract id for slug lookup
    const parts = slug.split('~');
    const slugKey = parts.length === 1 ? slug : parts[1];
    if (normalizeModelKeyForSearchPath(modelName) === 'itemprogress') {
        return [];
    }
    // Unified KB: findBySlug (KnowledgeBase Lambda)
    const query = schema.getFindBySlugGQL(modelName);
    const typeName = schema.getTypeName(modelName === 'class' ? 'course' : modelName);
    const variables = {
        organizationID: organizationId || '',
        objectType: typeName,
        slug: slugKey,
        libraryIds: librariesToSearch.length > 0 ? librariesToSearch : undefined,
    };
    const request = {
        query,
        variables,
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    };
    console.log('loadBySlug: using pgvector (findBySlug)', request);
    const results = await getGQLAPI().graphql(request);
    const raw = results?.data?.findBySlug;
    if (!raw)
        return [];
    // AWSJSON can come back as string (from Lambda) or parsed object
    let item;
    try {
        item = typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
    catch {
        console.error('loadBySlug: failed to parse findBySlug response', raw);
        return [];
    }
    // Normalize organizationID (KB may return organization_id or organization.id)
    if (item && !item.organizationID) {
        item.organizationID = item.organization_id ?? item.organization?.id;
    }
    // Skip items missing required fields to avoid MST identifier errors
    if (!item?.id || (isOrganizationScoped(modelName) && !item?.organizationID)) {
        return [];
    }
    return [item];
}
/** Path 1: List against DynamoDB (listCourses, listMicrocasts, etc.). */
function buildListRequestDynamo(cachedList, organizationID, isPortalApi) {
    const filterByOrg = !isPortalApi && cachedList.isOrganizationScoped;
    const orgFilter = filterByOrg && organizationID != null
        ? { organizationID: { eq: organizationID } }
        : {};
    const filter = filterByOrg || JSON.stringify(cachedList.filter) !== '{}'
        ? { ...cachedList.filter, ...orgFilter } : undefined;
    return {
        query: schema.getListGQL(cachedList.modelName, isPortalApi),
        variables: {
            filter,
            sort: cachedList.sort,
            limit: getListPageSize(cachedList.modelName),
            nextToken: cachedList.nextToken ?? null,
        },
        listName: `list${schema.getPluralTypeName(cachedList.modelName)}`,
    };
}
/** Path: item progress (AdminSearch Lambda, Dynamo). */
function buildListRequestItemProgressSearch(cachedList, organizationID, isPortalApi) {
    if (cachedList.modelName !== 'itemProgress') {
        throw new Error('buildListRequestItemProgressSearch: model must be itemProgress');
    }
    if (!organizationID) {
        throw new Error('buildListRequestItemProgressSearch: organizationID required for itemProgress');
    }
    const from = cachedList.items.length > 0 ? cachedList.items.length : 0;
    const pageLimit = 50;
    const input = buildItemProgressSearchInput(organizationID, (cachedList.filter || {}), cachedList.sort ?? null, pageLimit, from);
    return {
        query: schema.getItemProgressSearchGQL('itemProgress', isPortalApi),
        variables: { input },
        listName: 'itemProgressSearch',
    };
}
/**
 * Searchable-style asset filters used `type`; map to SearchFilterInput.assetTypes for pgvector.
 */
function extractAssetTypesFromListFilter(f) {
    const out = [];
    const pushEq = (op) => {
        if (op && typeof op === 'object' && op !== null && 'eq' in op) {
            const v = op.eq;
            if (v != null && String(v).trim())
                out.push(String(v).trim().toLowerCase());
        }
    };
    pushEq(f.type);
    const and = f.and;
    if (Array.isArray(and)) {
        for (const clause of and) {
            if (clause && typeof clause === 'object' && clause !== null && 'type' in clause) {
                pushEq(clause.type);
            }
        }
    }
    if (out.length === 0)
        return undefined;
    return [...new Set(out)];
}
function normalizeProductOptionsRecord(p) {
    const out = {};
    for (const [k, v] of Object.entries(p)) {
        if (v == null)
            continue;
        out[String(k)] = String(v);
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Searchable-style product_type / flat arrays → SearchFilterInput.productTypes for pgvector. */
function extractProductTypesFromListFilter(f) {
    const out = [];
    const pushEq = (op) => {
        if (op && typeof op === 'object' && op !== null && 'eq' in op) {
            const v = op.eq;
            if (v != null && String(v).trim())
                out.push(String(v).trim().toLowerCase());
        }
    };
    pushEq(f.productType);
    if (Array.isArray(f.productTypes)) {
        for (const p of f.productTypes) {
            if (p != null && String(p).trim())
                out.push(String(p).trim().toLowerCase());
        }
    }
    const and = f.and;
    if (Array.isArray(and)) {
        for (const clause of and) {
            if (clause && typeof clause === 'object' && clause !== null) {
                const c = clause;
                if ('productType' in c)
                    pushEq(c.productType);
                if (Array.isArray(c.productTypes)) {
                    for (const p of c.productTypes) {
                        if (p != null && String(p).trim())
                            out.push(String(p).trim().toLowerCase());
                    }
                }
            }
        }
    }
    if (out.length === 0)
        return undefined;
    return [...new Set(out)];
}
function extractProductOptionsFromListFilter(f) {
    const raw = f.productOptions;
    if (raw == null)
        return undefined;
    if (typeof raw === 'string') {
        try {
            const p = JSON.parse(raw);
            return normalizeProductOptionsRecord(p);
        }
        catch {
            return undefined;
        }
    }
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        return normalizeProductOptionsRecord(raw);
    }
    return undefined;
}
/** Unified KB search(input: SearchInput!) via Lambda — shared by loadList and batchLoadFilter. */
function computePgVectorSearchInput(params, organizationID, isPortalApi) {
    const { modelName, isOrganizationScoped: listIsOrganizationScoped, filter, limit, from, } = params;
    warnDroppedFilterFields(modelName, filter);
    const typeName = schema.getTypeName(modelName);
    const filterByOrg = !isPortalApi && listIsOrganizationScoped;
    let sortNorm = params.sort ?? null;
    if (modelName === 'product' || modelName === 'webPage' || modelName === 'ecommerceCategory') {
        if (sortNorm?.field === 'title') {
            sortNorm = { ...sortNorm, field: 'name' };
        }
    }
    let organizationIDStr = filter?.organizationID ?? filter?.organizationId ?? '';
    let libraryIds = filter?.libraryIds ?? null;
    const combineOrganizationWithLibraries = filter?.combineOrganizationWithLibraries === true;
    let text = filter?.text ?? null;
    const searchableOnly = isPortalApi ? (filter?.searchableOnly !== false) : undefined;
    const includeDoNotIndex = !isPortalApi ? true : (filter?.includeDoNotIndex === true);
    let searchInstructors = filter?.searchInstructors ?? null;
    if (!libraryIds && filter?.and && Array.isArray(filter.and)) {
        const libOr = filter.and.find((a) => {
            const ao = a;
            const orArr = ao?.or;
            return Array.isArray(orArr)
                && orArr.some((o) => o.libraries?.matchPhrase != null);
        });
        if (libOr && Array.isArray(libOr.or)) {
            libraryIds = libOr.or
                .map((o) => o.libraries?.matchPhrase)
                .filter(Boolean);
        }
    }
    if (!organizationIDStr && filter?.organizationID && typeof filter.organizationID === 'object') {
        organizationIDStr = filter.organizationID?.eq ?? '';
    }
    if (!organizationIDStr && filter?.and && Array.isArray(filter.and)) {
        const findOrgId = (arr) => {
            const found = arr.reduce((acc, a) => {
                if (acc)
                    return acc;
                if (typeof a === 'object' && a !== null) {
                    const obj = a;
                    if (obj.organizationID && typeof obj.organizationID === 'object') {
                        const eq = obj.organizationID?.eq;
                        if (eq)
                            return eq;
                    }
                    if (Array.isArray(obj.or))
                        return findOrgId(obj.or);
                }
                return null;
            }, null);
            return found ?? '';
        };
        organizationIDStr = findOrgId(filter.and);
    }
    if (!libraryIds && filterByOrg && organizationID) {
        organizationIDStr = organizationID;
    }
    else if (!libraryIds && !isPortalApi
        && isOrganizationScoped(modelName) && organizationID) {
        organizationIDStr = organizationID;
    }
    if (!text && filter?.and && Array.isArray(filter.and)) {
        const searchOr = filter.and.find((a) => {
            const ao = a;
            const orArr = ao?.or;
            const hasMatchPrefix = (o) => typeof o === 'object' && o != null
                && Object.values(o).some((v) => v?.matchPhrasePrefix != null);
            return Array.isArray(orArr) && orArr.some(hasMatchPrefix);
        });
        const searchOrOr = searchOr?.or;
        if (searchOr && Array.isArray(searchOrOr)) {
            const orArr = searchOrOr;
            const hasPrefix = (v) => v?.matchPhrasePrefix != null;
            const entry = orArr.find((o) => Object.values(o).some(hasPrefix));
            const val = entry && Object.values(entry)[0]?.matchPhrasePrefix;
            if (val)
                text = val;
        }
    }
    if (!searchInstructors && filter?.or && Array.isArray(filter.or)) {
        const firstOr = filter.or[0];
        const si = firstOr?.searchInstructors?.matchPhrase;
        if (si)
            searchInstructors = si;
    }
    const orgIdForFilter = modelName === 'organization' && !organizationIDStr && !libraryIds
        ? (organizationID ?? '00000000-0000-0000-0000-000000000000')
        : organizationIDStr;
    const assetTypes = modelName === 'asset' ? extractAssetTypesFromListFilter(filter) : undefined;
    const productTypes = modelName === 'product' ? extractProductTypesFromListFilter(filter) : undefined;
    const productOptions = modelName === 'product' ? extractProductOptionsFromListFilter(filter) : undefined;
    const productSearch = modelName === 'product' && productOptions != null
        && Object.keys(productOptions).length > 0;
    const topicID = extractEqFilterField(filter, 'topicID');
    const postStatus = extractEqFilterField(filter, 'status');
    const filterPayload = {
        ...(isPortalApi ? {} : { organizationID: orgIdForFilter }),
        libraryIds,
        ...(combineOrganizationWithLibraries && Array.isArray(libraryIds) && libraryIds.length > 0
            ? { combineOrganizationWithLibraries: true }
            : {}),
        ...(searchableOnly != null && { searchableOnly }),
        ...(includeDoNotIndex != null && { includeDoNotIndex }),
        searchInstructors,
        ...(assetTypes?.length ? { assetTypes } : {}),
        ...(productTypes?.length ? { productTypes } : {}),
        ...(productOptions != null && Object.keys(productOptions).length > 0
            ? { productOptions: JSON.stringify(productOptions) }
            : {}),
        ...(topicID ? { topicID } : {}),
        ...(postStatus ? { status: postStatus } : {}),
    };
    return {
        objectType: typeName,
        text,
        ...(productSearch ? { productSearch: true } : {}),
        filter: filterPayload,
        sort: sortNorm,
        limit,
        from,
    };
}
/** Path: unified KB search (pgvector). */
function buildListRequestPgVector(cachedList, organizationID, isPortalApi) {
    const from = cachedList.items.length > 0 ? cachedList.items.length : 0;
    const limit = getListPageSize(cachedList.modelName);
    const input = computePgVectorSearchInput({
        modelName: cachedList.modelName,
        isOrganizationScoped: cachedList.isOrganizationScoped,
        filter: cachedList.filter,
        sort: cachedList.sort ?? null,
        limit,
        from,
    }, organizationID, isPortalApi);
    return {
        query: schema.getSearchGQL(cachedList.modelName, isPortalApi),
        variables: { input },
        listName: 'search',
    };
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** pgvector KB uses uuid organization_id; legacy org ids (e.g. test-organization) must use Dynamo list. */
function isValidUuid(value) {
    const s = String(value ?? '').trim();
    return s.length > 0 && UUID_RE.test(s);
}
function resolveListSearchPath(cachedList, organizationID = null) {
    if (!cachedList.hasSearch())
        return 'list';
    // Legacy non-uuid org ids are not indexed in pgvector; Dynamo list still works.
    if (organizationID && !isValidUuid(organizationID))
        return 'list';
    const forced = getForcedSearchPath(cachedList.modelName);
    if (forced === 'itemProgressSearch')
        return 'itemProgressSearch';
    if (forced === 'pgvector')
        return 'pgvector';
    // Organizations: keep the default listing on Dynamo so deletion status is always current;
    // only route to pgvector search when the user has actually typed a text query.
    if (normalizeModelKeyForSearchPath(cachedList.modelName) === 'organization'
        && !filterContainsTextSearch(cachedList.filter)) {
        return 'list';
    }
    const incompatibleFields = SEARCH_INCOMPATIBLE_FILTER_FIELDS_BY_MODEL[normalizeModelKeyForSearchPath(cachedList.modelName)];
    if (incompatibleFields?.some((field) => filterContainsField(cachedList.filter, field))) {
        return 'list';
    }
    return 'pgvector';
}
/**
 * Loads a list of items: one of three paths.
 * 1. List (DynamoDB) – listCourses, listMicrocasts, etc.; no search.
 * 2. Search (PG Vector) – unified search(input: SearchInput!) via Lambda/KB.
 * 3. itemProgressSearch – admin progress report (AdminSearch → AdminSearchHandler, RDS).
 *
 * @returns
 */
export async function loadList(cachedList, organizationID = null, isPortalApi = false) {
    if (!isPortalApi && cachedList.isOrganizationScoped && !organizationID) {
        throw Error(`Missing organization when loading: ${cachedList.id}`);
    }
    const listPath = resolveListSearchPath(cachedList, organizationID);
    let result;
    if (listPath === 'list') {
        result = buildListRequestDynamo(cachedList, organizationID, isPortalApi);
    }
    else if (listPath === 'itemProgressSearch') {
        result = buildListRequestItemProgressSearch(cachedList, organizationID, isPortalApi);
    }
    else {
        result = buildListRequestPgVector(cachedList, organizationID, isPortalApi);
    }
    const { query, variables, listName } = result;
    // Portal API: unified search always uses AWS_IAM so console/CLI can test with IAM.
    // Admin: list uses IAM unless model needs Cognito; pgvector search uses Cognito.
    let authMode = GRAPHQL_AUTH_MODE.AWS_IAM;
    if (isPortalApi && listPath !== 'list') {
        // keep AWS_IAM for portal search*
    }
    else if (listPath !== 'list' && !isPortalApi) {
        authMode = GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS;
    }
    else if (cachedList.useCognitoAuthMode()) {
        authMode = GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS;
    }
    const request = { query, variables, authMode };
    const results = await getGQLAPI().graphql(request);
    const { data, errors } = results;
    if (errors?.length) {
        const msg = errors.map((e) => e.message || e.errorType).join('; ');
        throw new Error(msg);
    }
    const rawItems = (data?.[listName]?.items) ?? [];
    // AWSJSON scalar returns items as JSON strings; parse if needed
    let items = rawItems.map((it) => {
        if (typeof it === 'string') {
            try {
                return JSON.parse(it);
            }
            catch {
                return {};
            }
        }
        return it ?? {};
    });
    // PG Vector path: for types where search already returns full item (display_json), use as-is.
    // Otherwise merge from batchGetModel (DynamoDB).
    if (listPath === 'pgvector'
        && items.length > 0
        && isOrganizationScoped(cachedList.modelName)
        && !FULL_ITEM_FROM_SEARCH_MODELS.has(cachedList.modelName)) {
        /** Match KB slim.id to Dynamo id despite UUID casing differences. */
        const normId = (id) => (typeof id === 'string' ? id.trim().toLowerCase() : '');
        /** Dynamo PK org for each hit; KB rows use organization_id (library search can return other orgs). */
        const orgForSlim = (slim) => {
            const v = slim.organizationID ?? slim.organizationId;
            if (typeof v === 'string' && v.trim())
                return v.trim();
            return organizationID ?? null;
        };
        const mergeFullIntoById = (byId, fullItems) => {
            fullItems.forEach((full) => {
                const k = normId(full?.id);
                if (k)
                    byId.set(k, full);
            });
        };
        const byOrg = new Map();
        for (const slim of items) {
            const id = slim.id;
            if (!id)
                continue;
            const oid = orgForSlim(slim);
            if (!oid)
                continue;
            if (!byOrg.has(oid))
                byOrg.set(oid, new Set());
            byOrg.get(oid).add(id);
        }
        if (byOrg.size > 0) {
            const fullLists = await Promise.all([...byOrg.entries()].map(([oid, idSet]) => batchLoadHelper(cachedList.modelName, oid, [...idSet], isPortalApi)));
            const byId = new Map();
            mergeFullIntoById(byId, fullLists.flat());
            const missingIds = (slims) => {
                const out = [];
                for (const slim of slims) {
                    const k = normId(slim.id);
                    if (!k || byId.has(k))
                        continue;
                    const sid = typeof slim.id === 'string' ? slim.id.trim() : '';
                    if (sid)
                        out.push(sid);
                }
                return [...new Set(out)];
            };
            // KB organization_id can be wrong vs Dynamo; retry under the admin org context.
            let retryIds = missingIds(items);
            if (retryIds.length > 0 && organizationID) {
                const second = await batchLoadHelper(cachedList.modelName, organizationID, retryIds, isPortalApi);
                mergeFullIntoById(byId, second);
            }
            retryIds = missingIds(items);
            if (retryIds.length > 0) {
                console.warn('loadList: batchGetModel still missing after org-scoped and current-org retry', { modelName: cachedList.modelName, ids: retryIds });
            }
            items = items.map((slim) => {
                const k = normId(slim.id);
                const full = k ? byId.get(k) : undefined;
                if (full) {
                    if (cachedList.modelName === 'asset' && slim.type != null) {
                        return { ...full, type: slim.type };
                    }
                    return full;
                }
                console.warn(`loadList: using pgvector search row for ${cachedList.modelName} id ${String(slim.id)} ` +
                    '(batchGetModel returned no Dynamo row; check KB vs Dynamo org/id or stale index).');
                return slim;
            });
        }
    }
    return {
        items,
        nextToken: data[listName]?.nextToken,
        total: data[listName]?.total,
        aggregates: data[listName]?.aggregates ?? null,
    };
}
/**
 * Ids per `batchGetModel` call. Full records for some models (e.g. course) are large, and the
 * AdminSearch Lambda's response is capped at 6 MB, so one call for ~100 ids can fail with a 413.
 */
export const BATCH_LOAD_SIZE = 25;
/** Max `batchGetModel` calls in flight at once for one `batchLoadHelper` call. */
export const BATCH_LOAD_CONCURRENCY = 4;
/**
 * Returns results of multiple items of a single type.
 *
 * Ids are fetched in batches of `BATCH_LOAD_SIZE` with bounded concurrency; rows come back in batch
 * order. A failing batch is logged and skipped so the other batches' rows are still returned.
 *
 * @returns
 */
export async function batchLoadHelper(modelName, organizationID, ids, isPortalApi = false) {
    if (ids.length === 0)
        return [];
    const batchFieldName = schema.getBatchGetFieldName(modelName);
    const query = schema.getBatchLoadGQL(modelName, isPortalApi);
    const authMode = isPortalApi
        ? GRAPHQL_AUTH_MODE.AWS_IAM
        : GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS;
    const loadBatch = async (batchIds, batchIndex) => {
        const request = {
            query,
            variables: { modelName: batchFieldName, ids: batchIds, organizationID },
            authMode,
        };
        try {
            const results = await getGQLAPI().graphql(request);
            const { data, errors } = results;
            if (errors?.length) {
                console.error('Error on batchLoad', {
                    modelName, organizationID, batchIndex, batchSize: batchIds.length, errors,
                });
            }
            const rows = data?.batchGetModel?.[batchFieldName];
            return Array.isArray(rows) ? rows : [];
        }
        catch (e) {
            console.error('Error on batchLoad', {
                modelName, organizationID, batchIndex, batchSize: batchIds.length, error: e,
            });
            return [];
        }
    };
    const batches = chunkArray(ids, BATCH_LOAD_SIZE);
    const batchRows = new Array(batches.length);
    let nextBatch = 0;
    // Each worker takes the next unclaimed batch; results are stored by index to keep batch order.
    const worker = async () => {
        while (nextBatch < batches.length) {
            const batchIndex = nextBatch;
            nextBatch += 1;
            // Sequential within a worker is the point: it bounds concurrency.
            // eslint-disable-next-line no-await-in-loop
            batchRows[batchIndex] = await loadBatch(batches[batchIndex], batchIndex);
        }
    };
    await Promise.all(Array.from({ length: Math.min(BATCH_LOAD_CONCURRENCY, batches.length) }, worker));
    return batchRows.flat();
}
export const gqlCall = async (gql, data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(gql, data));
    return response;
};
const executeEngagementFlowAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(executeEngagementFlow, data));
    if (response && response.data && response.data.executeEngagementFlow) {
        if (response.data.executeEngagementFlow.statusCode === 200) {
            return response.data.executeEngagementFlow.body;
        }
        return false;
    }
    return false;
};
const syncOrganizationToCognitoAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(syncOrganizationToCognitoGQL, data));
    return response.data.syncOrganizationToCognito;
};
const verifyCheckoutAction = async (portalApi, data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(verifyCheckoutGQL(portalApi), data));
    return response.data.verifyCheckout;
};
const syncDriveFilesAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(syncDriveFilesGQL, data));
    return response.data.syncDriveFiles;
};
const getGoogleAccessTokenAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(getGoogleAccessTokenGQL, data));
    return response.data.getGoogleAccessToken;
};
const getGoogleSyncConfigAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(getGoogleSyncConfigGQL, data));
    return response.data.getGoogleSyncConfig;
};
const getAllAISessionsAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(getAllAISessionsGQL, data));
    return response.data.getAllAISessions;
};
const getAISessionAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(getAISessionGQL, data));
    return response.data.getAISession;
};
export const runQueryAction = async (data) => {
    const response = await getGQLAPI().graphql(graphqlOperation(runQueryGQL, data));
    return response.data.runQuery;
};
/**
 * Value for `condition: { updatedAt: { eq } }`. Prefer the exact AWSDateTime string from
 * GraphQL/Dynamo; re-formatting via Date can change fractional seconds and fail the check.
 */
function awsDateTimeForCondition(value) {
    if (value == null)
        return value;
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return new Date(value).toISOString();
    }
    return value;
}
/** Fresh Dynamo updatedAt before conditional update (list/search rows may be stale). */
async function refreshDiscussionPostConditionUpdatedAt(self, target) {
    const { organizationID: orgFromKey, id } = splitKey(target.key ?? '', self.currentOrganization?.id ?? '');
    const orgId = target.organization?.id ?? orgFromKey ?? self.currentOrganization?.id;
    if (!orgId || !id)
        return undefined;
    const query = schema.getItemGQL('discussionPost', self.isPortalApi);
    const typeName = schema.getTypeName('discussionPost');
    const result = await getGQLAPI().graphql({
        query,
        variables: { id, organizationID: orgId },
        authMode: self.isPortalApi
            ? GRAPHQL_AUTH_MODE.AWS_IAM
            : GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
    });
    const remote = result?.data?.[`get${typeName}`];
    const raw = remote?.updatedAt;
    return raw != null ? awsDateTimeForCondition(raw) : undefined;
}
const updateFunction = (self) => (function* update(modelName, target, clearCache = true, portalAIConfigInput) {
    if (portalAIConfigInput && modelName !== 'portal')
        throw Error('AI configuration input requires a Portal');
    const submittedSnapshot = portalAIConfigInput && schema.getPortalAIConfigSource(target)?.snapshot;
    if (portalAIConfigInput && !submittedSnapshot)
        throw Error('Reload this portal before saving its configuration.');
    const submittedChangeCount = target.changeCount;
    let lastUpdatedAt = awsDateTimeForCondition(target.updatedAt);
    if (modelName === 'discussionPost') {
        const freshUpdatedAt = yield refreshDiscussionPostConditionUpdatedAt(self, target);
        if (freshUpdatedAt != null) {
            lastUpdatedAt = freshUpdatedAt;
        }
    }
    const prepareCategories = portalAIConfigInput
        && Object.prototype.hasOwnProperty.call(portalAIConfigInput.input, 'categories');
    if (target.preUpdate && (!portalAIConfigInput || prepareCategories)) {
        try {
            const preparation = target.preUpdate(false);
            if (portalAIConfigInput && prepareCategories) {
                portalAIConfigInput = {
                    ...portalAIConfigInput,
                    input: { ...portalAIConfigInput.input, categories: schema.getValuesForUpdate('portal', target, self.isPortalApi, false).categories },
                    snapshot: { ...portalAIConfigInput.snapshot, categories: getSnapshot(target).categories },
                };
            }
            yield preparation;
        }
        catch (error) {
            console.log(`Error updating ${modelName}`, error);
            return false;
        }
    }
    if (!portalAIConfigInput)
        target.updateValues({ updatedAt: new Date() });
    console.log(`Updating ${modelName}`, target);
    console.log('Last updated', lastUpdatedAt);
    const query = schema.getUpdateGQL(modelName, self.isPortalApi);
    const input = schema.getValuesForUpdate(modelName, target, self.isPortalApi, false);
    if (portalAIConfigInput) {
        const { id, organizationID } = input;
        Object.keys(input).forEach((key) => { delete input[key]; });
        Object.assign(input, portalAIConfigInput.input, { id, organizationID, updatedAt: new Date().toISOString() });
    }
    try {
        const result = yield getGQLAPI().graphql({
            query,
            variables: {
                input,
                condition: { updatedAt: { eq: lastUpdatedAt } },
            },
        });
        console.log('updated model', result);
        if (portalAIConfigInput) {
            const hasLaterEdits = target.changeCount !== submittedChangeCount;
            const saved = result?.data?.updatePortal;
            const config = saved && Object.prototype.hasOwnProperty.call(saved, 'clientAIConfig')
                ? (typeof saved.clientAIConfig === 'string' ? JSON.parse(saved.clientAIConfig) : saved.clientAIConfig)
                : portalAIConfigInput.config;
            const updatedAt = new Date(saved?.updatedAt || input.updatedAt);
            const snapshot = { ...submittedSnapshot };
            const properties = schema.getModel('portal').properties;
            Object.entries(properties).forEach(([field, definition]) => {
                if (Object.prototype.hasOwnProperty.call(input, definition.fieldName || field)) {
                    snapshot[field] = portalAIConfigInput.snapshot[field];
                }
            });
            const current = getSnapshot(target);
            const hasUnsubmittedEdits = Object.entries(properties).some(([field, definition]) => !definition.readOnly && !isEqual(current[field], snapshot[field]));
            snapshot.updatedAt = updatedAt.getTime();
            schema.acceptPortalAIConfigSave(target, config, snapshot, portalAIConfigInput.changes);
            target.updateValues({ updatedAt });
            if (!hasLaterEdits && !hasUnsubmittedEdits)
                target.resetChange();
        }
        else {
            target.resetChange();
        }
        if (clearCache) {
            self.clearCachedList(modelName);
        }
    }
    catch (error) {
        console.log(`Error updating ${modelName}`, {
            query,
            error,
            input,
        });
        // Handle specific concurrency errors
        if (error.errors?.some((e) => e.errorType === 'DynamoDB:ConditionalCheckFailedException')) {
            console.log('Concurrency error detected');
            navStore.errorDialog.showError('Concurrency Error', 'The document has been modified by another user. Your changes have been preserved but not saved. You may want to refresh the page to see the latest changes before saving again.');
            return { success: false, reason: 'concurrency' };
        }
        // General error
        navStore.errorDialog.showError('Error Saving Data', `There was an error saving the data for ${modelName}: ${target.name}. Please try again.`);
        return { success: false, reason: 'general' };
    }
    return { success: true };
});
const processVideoDuration = async (file) => (new Promise((resolve) => {
    if (!file.type.includes('video')) {
        resolve('');
    }
    // Check video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    // eslint-disable-next-line func-names
    video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        if (video.duration > 0) {
            const duration = parseInt(video.duration.toFixed(0), 10);
            let minutes = Math.floor(duration / 60);
            let seconds = duration;
            let hours = 0;
            if (minutes > 60) {
                hours = Math.floor(minutes / 60);
                minutes -= (hours * 60);
            }
            if (minutes > 0 || hours > 0) {
                seconds = (duration) - (hours * 3600) - (minutes * 60);
            }
            resolve(`${hours}:${minutes}:${seconds}`);
        }
        else {
            resolve('');
        }
    };
    video.src = URL.createObjectURL(file);
}));
const batchLoadWithDependencies = async (organizationId, modelName, filter, sort, dependencies = [], itemsPerPage = 500) => {
    let allItem = [];
    let hasMorePages = true;
    let from = 0;
    const parseSearchItems = (raw) => raw.map((it) => {
        if (typeof it === 'string') {
            try {
                return JSON.parse(it);
            }
            catch {
                return {};
            }
        }
        return it ?? {};
    });
    while (hasMorePages) {
        const isItemProgress = modelName === 'itemProgress';
        const query = isItemProgress
            ? schema.getItemProgressSearchGQL('itemProgress', false)
            : schema.getSearchGQL(modelName, false);
        const variables = isItemProgress
            ? {
                input: buildItemProgressSearchInput(organizationId, filter, sort, itemsPerPage, from),
            }
            : {
                input: computePgVectorSearchInput({
                    modelName,
                    isOrganizationScoped: isOrganizationScoped(modelName),
                    filter: (filter || {}),
                    sort,
                    limit: itemsPerPage,
                    from,
                }, organizationId, false),
            };
        const request = {
            query,
            variables,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
        };
        // eslint-disable-next-line no-await-in-loop
        const results = await getGQLAPI().graphql(request);
        const { data } = results;
        const listName = isItemProgress ? 'itemProgressSearch' : 'search';
        const rawItems = data[listName]?.items || [];
        const items = isItemProgress ? rawItems : parseSearchItems(rawItems);
        // BEGIN: Large $or clause splitting
        const MAX_OR_CLAUSE = 100;
        const baseAnd = filter?.and || [];
        const orClause = baseAnd.find((c) => c.or);
        const baseFilterWithoutOr = {
            and: baseAnd.filter((f) => !f.or),
        };
        if (orClause?.or?.length > MAX_OR_CLAUSE) {
            const chunks = chunkArray(orClause.or, MAX_OR_CLAUSE);
            let chunkedResults = [];
            /* eslint-disable no-restricted-syntax -- sequential async accumulation */
            for (const chunk of chunks) {
                const chunkedFilter = {
                    and: [
                        ...baseFilterWithoutOr.and,
                        { or: chunk },
                    ],
                };
                const variablesChunk = isItemProgress
                    ? {
                        input: buildItemProgressSearchInput(organizationId, chunkedFilter, sort, itemsPerPage, 0),
                    }
                    : {
                        input: computePgVectorSearchInput({
                            modelName,
                            isOrganizationScoped: isOrganizationScoped(modelName),
                            filter: chunkedFilter,
                            sort,
                            limit: itemsPerPage,
                            from: 0,
                        }, organizationId, false),
                    };
                const requestChunk = {
                    query,
                    variables: variablesChunk,
                    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                };
                // eslint-disable-next-line no-await-in-loop
                const resultsChunk = await getGQLAPI().graphql(requestChunk);
                const rawChunkItems = resultsChunk.data[listName]?.items || [];
                const chunkedItems = isItemProgress ? rawChunkItems : parseSearchItems(rawChunkItems);
                chunkedResults = chunkedResults.concat(chunkedItems);
            }
            allItem = allItem.concat(chunkedResults);
            break;
        }
        // END: Large $or clause splitting
        const totalCount = data[listName]?.total != null ? Number(data[listName].total) : null;
        /** Lambda caps LIMIT below requested itemsPerPage (e.g. 100); must not advance OFFSET by itemsPerPage. */
        const rawNextToken = data[listName]?.nextToken;
        allItem = allItem.concat(items);
        if (isItemProgress) {
            from = allItem.length;
        }
        else {
            const parsedNext = rawNextToken != null && rawNextToken !== ''
                ? Number.parseInt(String(rawNextToken), 10)
                : Number.NaN;
            if (!Number.isNaN(parsedNext)) {
                from = parsedNext;
            }
            else {
                from += items.length;
            }
        }
        if (!isItemProgress && items.length === 0) {
            hasMorePages = false;
        }
        else {
            hasMorePages = totalCount != null && allItem.length < totalCount;
        }
    }
    const dependenciesMap = {};
    allItem.forEach((item) => {
        dependencies.forEach((dep) => {
            const depID = item[`${dep}ID`];
            const { id, organizationID } = splitKey(depID, organizationId);
            if (id) {
                if (!dependenciesMap[dep]) {
                    dependenciesMap[dep] = new Map();
                }
                if (!dependenciesMap[dep].has(organizationID)) {
                    dependenciesMap[dep].set(organizationID, []);
                }
                dependenciesMap[dep].get(organizationID)?.push({ id, organizationID });
            }
        });
    });
    const loadedDependencies = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const dep of dependencies) {
        const orgIdGroups = dependenciesMap[dep];
        if (orgIdGroups) {
            // eslint-disable-next-line no-restricted-syntax
            for (const [orgID, idOrgPairs] of orgIdGroups) {
                const chunks = chunkArray(idOrgPairs, 20);
                const promises = chunks.map(async (chunk) => {
                    const ids = chunk.map((pair) => pair.id);
                    const batchResults = await batchLoadHelper(dep, orgID, ids);
                    batchResults.forEach((obj) => {
                        loadedDependencies[dep] = loadedDependencies[dep] || {};
                        loadedDependencies[dep][obj.id] = obj;
                    });
                });
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(promises);
            }
        }
    }
    return allItem.map((item) => {
        const newItem = { ...item };
        dependencies.forEach((dep) => {
            const depID = item[`${dep}ID`];
            const { id: depKey } = splitKey(depID, organizationId);
            if (depID && loadedDependencies[dep]) {
                const depObject = loadedDependencies[dep][depID]
                    || (depKey && loadedDependencies[dep][depKey]);
                if (depObject) {
                    newItem[dep] = { ...depObject };
                }
            }
        });
        return newItem;
    });
};
/**
 * This is a map of active loads.  Items are added before call to server
 * and removed when the response is received.
 */
const loadItemMap = new Map();
export const getLoadItemMapSize = () => (loadItemMap.size);
export const MainModel = types
    .model('MainModel', {
    status: types.enumeration('Status', ['not_authenticated', 'not_loaded', 'loading', 'ready', 'error']),
    loadMessage: types.optional(types.string, () => (`Create ${Date.now()}`)),
    state: types.enumeration('State', ['pending', 'done', 'error']),
    portalClientSidePath: types.maybe(types.string),
    isPortalApi: types.optional(types.boolean, false),
    isAnonymous: types.optional(types.boolean, false), // used on portal
    /** Same value as `AMPLIFY_ENV` / deployment env (e.g. huwdev, prod); from aws_user_files_s3_bucket name. */
    amplifyEnvironment: types.optional(types.string, ''),
    // conversations: types.map(Conversation),
    currentAttendee: types.maybe(ChimeAttendeeType),
    currentAccess: types.maybe(types.frozen(JsonType)),
    // currentConversation: types.maybe(Conversation),
    currentCourse: types.maybe(types.reference(Course)),
    currentCourseProgresses: types.array(resolvableReferenceType('courseProgress')),
    currentCustomer: types.maybe(types.reference(Customer)),
    currentDocumentProgresses: types.array(resolvableReferenceType('documentProgress')),
    currentHub: types.maybe(types.reference(Hub)),
    currentLead: types.maybe(types.reference(Lead)),
    currentLearningBoard: types.maybe(types.reference(LearningBoard)),
    currentLibrary: types.maybe(types.reference(Library)),
    currentMeeting: types.maybe(types.reference(Meeting)),
    currentMeetingRegistrations: types.array(resolvableReferenceType('meetingRegistration')),
    currentMembers: types.array(resolvableReferenceType('member')),
    currentMicrocastProgresses: types.array(resolvableReferenceType('microcastProgress')),
    currentOrganization: types.maybe(types.reference(Organization)),
    currentOrganizations: types.array(types.reference(Organization)),
    currentPortal: types.maybe(types.reference(Portal)),
    currentProgramProgresses: types.array(resolvableReferenceType('programProgress')),
    currentProgressLinks: types.map(ItemProgressLink),
    currentRegistration: types.maybe(types.reference(Registration)),
    currentRepository: types.maybe(types.reference(Repository)),
    currentShopifyConfig: types.maybe(types.reference(ShopifyConfig)),
    currentUser: types.maybe(types.reference(User)),
    currentUserOrganizations: types.array(types.reference(UserOrganization)),
    currentUserRoles: types.optional(types.array(types.string), []),
    // customerContext: types.maybe(CustomerContext),
    combinedLibraries: types.array(types.reference(types.union(Library, LibraryShare))),
    receivedLibraries: types.array(types.reference(LibraryShare)),
    cachedLists: types.map(CachedListModel),
    identityId: types.maybe(types.string),
    assets: types.map(Asset),
    categories: types.map(Category),
    certificates: types.map(Certificate),
    certifications: types.map(Certification),
    clientAis: types.map(ClientAi),
    courses: types.map(Course),
    courseProgresses: types.map(CourseProgress),
    customers: types.map(Customer),
    discussionTopics: types.map(Topic),
    discussionPosts: types.map(Post),
    documents: types.map(Document),
    documentProgresses: types.map(DocumentProgress),
    emailTemplates: types.map(EmailTemplate),
    engagementFlows: types.map(EngagementFlow),
    hubs: types.map(Hub),
    hubCategories: types.map(HubCategory),
    instructors: types.map(Instructor),
    items: types.map(Item),
    itemProgresses: types.map(ItemProgress),
    learningBoards: types.map(LearningBoard),
    leads: types.map(Lead),
    profiles: types.map(Profile),
    libraries: types.map(Library),
    libraryShares: types.map(LibraryShare),
    meetings: types.map(Meeting),
    meetingRegistrations: types.map(Registration),
    members: types.map(Member),
    memberships: types.map(Membership),
    microcasts: types.map(Microcast),
    microcastProgresses: types.map(MicrocastProgress),
    organizations: types.map(Organization), // limited to those current user has acces to
    payments: types.map(Payment),
    portals: types.map(Portal),
    products: types.map(Product),
    ecommerceCategories: types.map(EcommerceCategory),
    webPages: types.map(WebPage),
    programs: types.map(Program),
    programProgresses: types.map(ProgramProgress),
    questions: types.map(Question),
    questionnaires: types.map(Questionnaire),
    repositories: types.map(Repository),
    shopifyConfigs: types.map(ShopifyConfig),
    userOrganizations: types.map(UserOrganization),
    users: types.map(User),
    kickers: types.map(Kicker),
    slugCache: types.map(types.string),
})
    .views((self) => {
    /**
     * Returns the map of items of the specified type. Throws an error
     * if the map doens't exist.
     *
     * @param modelName
     * @returns the MST map for the modelName
     */
    const getMapOf = (modelName) => {
        const pluralName = schema.getPluralName(modelName);
        if (!pluralName) {
            throw Error(`unknown modelName ${modelName}`);
        }
        const map = self[pluralName];
        if (!map) {
            throw Error(`unknown map for modelName ${modelName}, pluralName ${pluralName}`);
        }
        return map;
    };
    const getItem = (modelName, id) => getMapOf(modelName).get(id);
    const getBySlug = (modelName, slug) => (self.slugCache.get(`${modelName}/${slug}`));
    return {
        getMapOf,
        getItem,
        loggedIn: () => (Boolean(self.currentCustomer)),
        getBySlug,
    };
})
    .actions((self) => {
    // console.trace('MainModel actions');
    // tracks items we are looking for updates on
    // used for long running processes like video transcripts
    // and deployments
    let pollingList = [];
    let timerHandle = null;
    const removeItemFromPoll = (type, key) => {
        pollingList = pollingList.filter((entry) => (!(type === entry.type && key === entry.key)));
        if (pollingList.length === 0) {
            console.log('Clearing poll timer');
            clearInterval(timerHandle);
            timerHandle = null;
        }
    };
    const handlePollResults = (results) => {
        results?.forEach((result) => {
            // call the callback if it exists
            const pollItem = pollingList.find((entry) => (entry.type === result.type && entry.id === result.id));
            if (pollItem && pollItem.callback) {
                pollItem.callback(result[result.type]);
            }
            if (result.type === 'asset') {
                if (!result.asset) {
                    return;
                }
                const key = `${result.asset.organizationID}~${result.asset.id}`;
                const asset = self.getItem('asset', key);
                if (result.asset.status === 'processed') {
                    console.log('Its been processed');
                    removeItemFromPoll(result.type, key);
                    const priorStatus = asset.status;
                    schema.mapValuesFromGet('asset', result.asset, asset);
                    if (priorStatus !== asset.status) {
                        navStore.showSnackBar(`Processing complete: ${asset.name}`);
                    }
                }
            }
            else if (result.type === 'organization') {
                if (!result.organization) {
                    return;
                }
                console.log('get organization', result.organization);
            }
            else {
                console.log('Result not recognized');
            }
        });
    };
    const runPoll = () => {
        const pollRequest = {
            organizationID: self.currentOrganization?.id,
            items: pollingList.map((entry) => ({
                type: entry.type,
                id: entry.id,
                status: entry.status,
                updatedAt: entry.updatedAt,
            })),
        };
        getGQLAPI().graphql(graphqlOperation(checkStatusGQL(self.isPortalApi), pollRequest)).then((results) => {
            console.log('Poll Results', results);
            handlePollResults(results?.data?.checkStatus);
        }).catch((error) => {
            console.error('Error on polling', error);
        });
    };
    const addItemToPoll = (type, key, id, status, updatedAt, callback) => {
        pollingList.push({
            type,
            key,
            id,
            status,
            updatedAt,
            callback,
        });
        if (!timerHandle) {
            console.log('Starting poll timer');
            timerHandle = setInterval(() => {
                runPoll();
            }, 5000);
        }
    };
    // addItemToPoll('wee', 'bear', 'processed', new Date(), () => {});
    const setSlug = (modelName, slug, id) => {
        self.slugCache.set(`${modelName}/${slug}`, id);
    };
    const getProgressLink = (key) => {
        const l = self.currentProgressLinks.get(key);
        if (l) {
            return l;
        }
        const newLink = ItemProgressLink.create({ key });
        self.currentProgressLinks.put(newLink);
        return newLink;
    };
    const putProgressLink = (key, progress) => {
        let link = self.currentProgressLinks.get(key);
        if (!link) {
            link = ItemProgressLink.create({ key, progress });
            self.currentProgressLinks.put(link);
        }
        else {
            link.setProgress(progress);
        }
    };
    const processAsset = async (asset) => {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        try {
            const processResult = await getGQLAPI().graphql(graphqlOperation(processAssetGQL(self.isPortalApi), {
                assetID: asset.id,
                ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization.id }),
            }));
            schema.mapValuesFromGet('asset', processResult.data.processAsset, asset);
            console.log('Completed processing', asset);
            navStore.showSnackBar(`Processing complete: ${asset.name}`);
        }
        catch (e) {
            console.log('Error on process, do checks in background', e);
            addItemToPoll('asset', asset.key, asset.id, asset.status, asset.updatedAt, (updatedAsset) => {
                console.log('Ok, Im updated', updatedAsset);
            });
        }
        return asset;
    };
    return {
        addItemToPoll,
        removeItemFromPoll,
        setSlug,
        getProgressLink,
        putProgressLink,
        processAsset,
    };
})
    .actions((self) => ({
    setStatus(status) {
        console.log('Setting admin status', status);
        self.status = status;
    },
    setLoadMessage(message) {
        self.loadMessage = message;
    },
    setPortalClientSidePath(path) {
        self.portalClientSidePath = path;
    },
    setAnonymous(newValue) {
        self.isAnonymous = newValue;
    },
    create(modelName, key, name, fields) {
        // console.log(`create for ${modelName}:${key}`, fields);
        const tempKey = key || v4();
        let tempIdentifier;
        if (isOrganizationScoped(modelName)) {
            const keyParts = tempKey?.split('~');
            if (keyParts.length === 1) {
                tempIdentifier = {
                    organizationID: self.currentOrganization?.id,
                    id: tempKey,
                    key: `${self.currentOrganization?.id}~${tempKey}`,
                };
            }
            else {
                tempIdentifier = {
                    organizationID: keyParts[0],
                    id: keyParts[1],
                    key,
                };
            }
        }
        else {
            tempIdentifier = {
                id: tempKey,
                key,
            };
        }
        // this will make sure the item is created with correct defaults
        const newItem = schema.mapValuesFromGet(modelName, {
            ...tempIdentifier,
            name,
            ...fields,
        });
        const item = rootTypes.get(modelName).create(newItem);
        // console.log(`create for ${modelName}:resp`, item);
        return item;
    },
    stub(modelName, key, name, fields) {
        // console.log(`stub for ${modelName}:${key}`);
        const item = this.create(modelName, key, name, fields);
        // console.log('stubbed', getSnapshot(item));
        // console.log('stubbed-item', item);
        return self.getMapOf(modelName).put(item);
        // return item;
    },
}))
    .actions((self) => ({
    stubCachedList(modelName, filter, sort, crossOrganization) {
        const list = CachedListModel.create({
            id: getListKey(self.currentOrganization?.id, modelName, filter, sort, crossOrganization),
            modelName,
            items: [],
            // scope requests to current org if its an org scoped model and crossOrg is not specified
            isOrganizationScoped: isOrganizationScoped(modelName) && !crossOrganization,
            syncState: 'stubbed',
            filter,
            sort,
            moreItems: true,
            totalCount: -1,
            requestedCount: 0,
            pageSets: {},
            nextToken: null,
        });
        self.cachedLists.put(list);
        console.log('stubbed list', list);
        return list;
    },
    clearCachedLists() {
        self.cachedLists.forEach((cachedList) => cachedList.reset());
    },
    clearCachedList(modelName) {
        self.cachedLists.forEach((cachedList) => {
            if (modelName === cachedList.modelName) {
                cachedList.reset();
            }
        });
    },
    removeFromCachedList(modelName, item) {
        self.cachedLists.forEach((cachedList) => {
            if (cachedList.modelName === modelName) {
                // force a reload
                cachedList.removeItem(item);
            }
        });
    },
    fetchCachedList: flow(function* fetchCachedList(cachedList) {
        if (cachedList.syncState === 'loading') {
            // console.log(`already loading cached list for ${cachedList.id}`);
            return;
        }
        // console.log(`Fetching cachedList ${cachedList.id}`);
        while (cachedList.items.length < cachedList.requestedCount && cachedList.moreItems) {
            cachedList.syncState = 'loading';
            cachedList.setLoadError('');
            // console.log(`Loading cachedList ${cachedList.id},
            // currentItems: ${cachedList.items.length}, requestedItems: ${cachedList.requestedCount}`);
            self.state = 'pending';
            let newList;
            let nextToken;
            let total;
            let aggregates;
            try {
                const result = yield loadList(cachedList, self.currentOrganization?.id, self.isPortalApi);
                newList = result.items;
                nextToken = result.nextToken;
                total = result.total;
                aggregates = result.aggregates;
            }
            catch (error) {
                console.log('loadList error', error);
                let msg;
                if (error instanceof Error) {
                    msg = error.message;
                }
                else if (error && typeof error === 'object') {
                    const o = error;
                    if (typeof o.message === 'string')
                        msg = o.message;
                    else if (Array.isArray(o.errors) && o.errors[0] && typeof o.errors[0].message === 'string') {
                        msg = o.errors[0].message;
                    }
                    else
                        msg = 'An error occurred';
                }
                else {
                    msg = 'An error occurred';
                }
                cachedList.setLoadError(msg);
                cachedList.syncState = 'error';
                self.state = 'error';
                return;
            }
            cachedList.nextToken = nextToken ?? null;
            cachedList.totalCount = total || -1;
            if (typeof cachedList.setAggregates === 'function') {
                cachedList.setAggregates(aggregates ?? null);
            }
            self.state = 'done';
            const localItems = cast([]);
            try {
                const map = self.getMapOf(cachedList.modelName) || cast([]);
                newList.forEach((remoteItem) => {
                    const { key } = getIDsFromRemoteItem(cachedList.modelName, remoteItem);
                    const localItem = map.get(key) || self.stub(cachedList.modelName, key, 'loading...');
                    if (!localItem?.type && (cachedList.modelName === 'course' || cachedList.modelName === 'microcast')) {
                        localItem.type = cachedList.modelName;
                    }
                    // TODO: can move mapValuesFromGet to a snapshot preprocessor
                    const hasPendingPortalEdits = cachedList.modelName === 'portal'
                        && localItem.changeCount > 0 && schema.getPortalAIConfigSource(localItem);
                    if (!hasPendingPortalEdits) {
                        schema.mapValuesFromGet(cachedList.modelName, remoteItem, localItem);
                        localItem.resetLoadedAt();
                    }
                    localItems.push(localItem);
                });
            }
            catch (error) {
                console.log(`Error fetching cachedList of ${cachedList.id}`, error);
                // airbrake.notify(error);
                self.state = 'error';
            }
            cachedList.appendItems(localItems);
            cachedList.moreItems = (typeof nextToken === 'string')
                || (cachedList.totalCount > 0
                    && cachedList.totalCount > cachedList.items.length);
            // console.log('Set moreItems', cachedList.moreItems);
        }
        cachedList.syncState = 'loaded';
    }),
}))
    .actions((self) => ({
    reloadCachedList: flow(function* reloadCachedList(cachedList) {
        cachedList.reset();
        yield self.fetchCachedList(cachedList);
    }),
    /**
     * Reset AND re-fetch every cached list for a model.
     *
     * `clearCachedList` only resets matching lists to `stubbed`; it relies on a
     * subsequent `getCachedList` to refetch. Components such as `ItemNavigator`
     * hold their cached list in React state and do not re-call `getCachedList`
     * when the underlying list is reset, so those lists would otherwise stay
     * `stubbed` forever (a permanent loading spinner). Use this after a mutation
     * that changes which items exist (e.g. tenant deletion) so the visible list
     * actually reloads.
     */
    reloadCachedListsForModel: flow(function* reloadCachedListsForModel(modelName) {
        const lists = [];
        self.cachedLists.forEach((cachedList) => {
            if (cachedList.modelName === modelName)
                lists.push(cachedList);
        });
        lists.forEach((cachedList) => cachedList.reset());
        for (const cachedList of lists) {
            yield self.fetchCachedList(cachedList);
        }
    }),
    setCurrentUser(id, name) {
        self.currentUser = User.create({
            key: id,
            id,
            name,
        });
    },
    setCurrentCustomer(customerFields) {
        let localItem = self.getItem('customer', customerFields.id);
        if (!localItem) {
            localItem = self.stub('customer', customerFields.id, 'loading');
        }
        schema.mapValuesFromGet('customer', customerFields, localItem);
        localItem.resetLoadedAt();
        self.currentCustomer = localItem;
        self.currentHub = localItem?.hub || undefined;
    },
    setCurrentAccess(access) {
        self.currentAccess = access;
    },
    // setCurrentConversation(conversation:IConversation) {
    //   self.currentConversation = conversation;
    // },
    setCurrentCourse(course) {
        self.currentCourse = course;
    },
    setCurrentHub(hub) {
        self.currentHub = hub;
    },
    setCurrentRepository(repository) {
        self.currentRepository = repository;
    },
    setCurrentShopifyConfig(shopifyConfig) {
        self.currentShopifyConfig = shopifyConfig;
    },
    setCurrentLibrary(library) {
        self.currentLibrary = library;
    },
    setCurrentLearningBoad(learningBoard) {
        self.currentLearningBoard = learningBoard;
    },
    setCurrentMembers(members) {
        self.currentMembers = members;
    },
    setCurrentMeeting(meeting) {
        self.currentMeeting = meeting;
    },
    setCurrentMeetingRegistration(registration) {
        self.currentRegistration = registration;
    },
    setCurrentCourseProgresses(progresses) {
        const modelName = 'courseProgress';
        const map = self.getMapOf(modelName);
        self.currentCourseProgresses = progresses.map((remoteItem) => {
            const { id } = remoteItem;
            const localItem = map.get(id) || self.stub(modelName, id, 'loading...');
            schema.mapValuesFromGet(modelName, remoteItem, localItem);
            localItem.resetLoadedAt();
            self.putProgressLink(localItem.course.key, localItem);
            return localItem;
        });
    },
    setCurrentMeetingRegistrations(progresses) {
        const modelName = 'meetingRegistration';
        const map = self.getMapOf(modelName);
        self.currentMeetingRegistrations = progresses.map((remoteItem) => {
            const { id } = remoteItem;
            const localItem = map.get(id) || self.stub(modelName, id, 'loading...');
            schema.mapValuesFromGet(modelName, remoteItem, localItem);
            localItem.resetLoadedAt();
            self.putProgressLink(localItem.meeting.key, localItem);
            return localItem;
        });
    },
    setCurrentMicrocastProgresses(progresses) {
        const modelName = 'microcastProgress';
        const map = self.getMapOf(modelName);
        // console.log('setCurrentMicrocastProgresses:', progresses);
        self.currentMicrocastProgresses = progresses.map((remoteItem) => {
            const { id } = remoteItem;
            remoteItem.progress = remoteItem.progress ? JSON.parse(remoteItem.progress) : {};
            const localItem = map.get(id) || self.stub(modelName, id, 'loading...');
            schema.mapValuesFromGet(modelName, remoteItem, localItem);
            localItem.resetLoadedAt();
            self.putProgressLink(localItem.microcast.key, localItem);
            // console.log('localItem:', localItem);
            return localItem;
        });
    },
    setCurrentProgramProgresses(progresses) {
        const modelName = 'programProgress';
        const map = self.getMapOf(modelName);
        self.currentProgramProgresses = progresses.map((remoteItem) => {
            const { id } = remoteItem;
            console.log('updating progress:', id);
            const localItem = map.get(id) || self.stub(modelName, id, 'loading...');
            schema.mapValuesFromGet(modelName, remoteItem, localItem);
            localItem.resetLoadedAt();
            self.putProgressLink(localItem.program.key, localItem);
            return localItem;
        });
    },
    addCurrentCourseProgress(progress) {
        self.putProgressLink(progress.course.key, progress);
        const alreadyThere = self.currentCourseProgresses.find((p) => (p.id === progress.id));
        if (alreadyThere) {
            return;
        }
        self.currentCourseProgresses.push(progress);
    },
    addCurrentDocumentProgress(progress) {
        self.putProgressLink(progress.document.key, progress);
        const alreadyThere = self.currentDocumentProgresses.find((p) => (p.id === progress.id));
        if (alreadyThere) {
            return;
        }
        self.currentDocumentProgresses.push(progress);
    },
    addCurrentMeetingProgress(progress) {
        self.putProgressLink(progress.meeting.key, progress);
        const alreadyThere = self.currentMeetingRegistrations.find((p) => (p.id === progress.id));
        if (alreadyThere) {
            return;
        }
        self.currentMeetingRegistrations.push(progress);
    },
    addCurrentMicrocastProgress(progress) {
        self.putProgressLink(progress.microcast.key, progress);
        const alreadyThere = self.currentMicrocastProgresses.find((p) => (p.id === progress.id));
        if (alreadyThere) {
            return;
        }
        self.currentMicrocastProgresses.push(progress);
    },
    addCurrentProgramProgresses(progresses = []) {
        progresses.forEach((progress) => {
            const alreadyThere = self.currentProgramProgresses.find((p) => (p.id === progress.id));
            if (!alreadyThere) {
                // console.log('Add program progress', getSnapshot(progress));
                self.currentProgramProgresses.push(progress);
                self.putProgressLink(progress.program.key, progress);
            }
        });
    },
    setCurrentPortal(portal) {
        self.currentPortal = portal;
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('currentPortalID', portal.id);
        }
    },
    clearCurrentPortal() {
        self.currentPortal = undefined;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('currentPortalID');
        }
    },
    setIdentityId(id) {
        self.identityId = id;
    },
    setAmplifyEnvironment(newValue) {
        self.amplifyEnvironment = newValue;
    },
    publishToRepository: flow(function* publishToRepository(privateKey, repositoryID, repositoryPath) {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        self.state = 'pending';
        const result = yield getGQLAPI().graphql(graphqlOperation(publishToRepositoryMutation, {
            organizationID: self.currentOrganization.id,
            destinationRepository: repositoryID,
            destinationPath: (repositoryPath && repositoryPath.length > 0 ? `${repositoryPath}/${privateKey}` : privateKey),
            // @todo - not clear what the destinationPath is for?
            sourcePath: `private/${self.identityId}`,
            sourceName: privateKey,
        }));
        const assetSnap = result.data.publishToRepository;
        const asset = rootTypes.get('asset').create(schema.mapValuesFromGet('asset', assetSnap));
        self.getMapOf('asset').put(asset);
        console.log('Create asset', asset);
        yield self.processAsset(asset);
        return asset;
    }),
    setAccessLevel: flow(function* setAccessLevel(assetID, newLevel) {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        const processResult = yield getGQLAPI().graphql(graphqlOperation(setAccessLevelMutation, {
            assetID,
            organizationID: self.currentOrganization.id,
            newLevel,
        }));
        return processResult;
    }),
    /**
     * `getOrLoad` defers `load()` with `setTimeout(0)`, so a cached `loaded` item stays
     * observable as loaded for one turn and edit screens paint stale data then refresh.
     * Mirror `load()`'s early-return rules and set `syncState` to `loading` synchronously
     * when `load()` will actually refetch (same conditions as just before `localItem.syncState = 'loading'` in `load`).
     */
    syncLoadingStateBeforeDeferredLoad(modelName, key, maxAge) {
        const map = self.getMapOf(modelName);
        const localItem = map.get(key);
        if (!localItem) {
            return;
        }
        const pendingEdits = localItem.changeCount ?? 0;
        if (localItem.syncState === 'loaded' && pendingEdits > 0) {
            return;
        }
        const maxAgeSeconds = maxAge !== undefined && maxAge !== null ? maxAge : DEFAULT_MAX_AGE;
        if (localItem.syncState === 'loaded'
            && maxAgeSeconds > 0
            && (Date.now() - localItem.loadedAt) < maxAgeSeconds * 1000) {
            return;
        }
        const loadKey = `${modelName}:${key}`;
        if (loadItemMap.get(loadKey)) {
            return;
        }
        if (localItem.syncState !== 'loading') {
            localItem.syncState = 'loading';
        }
    },
    load: async (modelName, keyOrID, maxAge = DEFAULT_MAX_AGE) => {
        // console.log('MainModel.load', modelName, keyOrID, maxAge);
        if (!modelName || !keyOrID) {
            console.error('model and keyOrID are required');
            return undefined;
        }
        const key = resolveKey(keyOrID, self.currentOrganization?.id, modelName);
        const map = self.getMapOf(modelName);
        const localItem = map.get(key) || self.stub(modelName, key, 'loading...');
        const pendingEdits = localItem.changeCount ?? 0;
        if (localItem.syncState === 'loaded' && pendingEdits > 0) {
            return localItem;
        }
        const maxAgeSeconds = maxAge !== undefined && maxAge !== null ? maxAge : DEFAULT_MAX_AGE;
        if (localItem.syncState === 'loaded'
            && maxAgeSeconds > 0
            && (Date.now() - localItem.loadedAt) < maxAgeSeconds * 1000) {
            return localItem;
        }
        // console.log(
        //   'loading',
        //   {
        //     modelName,
        //     key,
        //     syncState: localItem.syncState,
        //     loadedAt: localItem.loadedAt,
        //   },
        // );
        /**
         * This is pretty complicated and should be reviewed/abstracted/cleaned-up
         * Basic idea is that a map of items currently being loaded is kept.
         * Before initiating a load this maps is checked and if it already
         * contains the promise for the item then it is simply returned.
         * If the map doesnt contain the item, a promise that resolves
         * when the item is created is returned, and added to the map.
         * When the promise resolves that item is removed from the map.
        */
        const loadKey = `${modelName}:${key}`;
        // check if loading
        const loadInProgress = loadItemMap.get(loadKey);
        if (loadInProgress) {
            // console.log(`Yield to existing: ${loadKey}, loadItemMap.size: ${loadItemMap.size}`);
            const item = await loadInProgress;
            return item;
        }
        // not being loaded yet - load it now
        // console.log(`Loading: ${loadKey}, loadItemMap.size: ${loadItemMap.size}`);
        localItem.syncState = 'loading';
        try {
            // map data to model object and return
            // self.state = 'done';
            const loadRequest = async (m, k) => {
                const remoteItem = await loadItem(m, k, self.currentOrganization?.id, self.isPortalApi);
                if (!remoteItem) {
                    console.log(`No ${loadKey} found.`);
                    localItem.setNotFound();
                    return localItem;
                }
                schema.mapValuesFromGet(modelName, remoteItem, localItem);
                localItem.resetLoadedAt();
                return localItem;
            };
            const loadRequestPromise = loadRequest(modelName, key);
            loadItemMap.set(loadKey, loadRequestPromise);
            // console.log(`${getTimestamp()} Loading: ${loadKey},
            // loadItemMap.size: ${loadItemMap.size}`);
            const item = await loadRequestPromise;
            loadItemMap.delete(loadKey);
            // console.log(`${getTimestamp()} Removed: ${loadKey},
            // loadItemMap.size: ${loadItemMap.size}`);
            return item;
        }
        catch (error) {
            console.error(`Error in load fetching ${modelName}:${keyOrID}`, error);
            localItem.setLoadError?.(error instanceof Error ? error : String(error));
        }
        return localItem;
    },
    batchLoadModel: async (mapKey, rawIds) => {
        const ids = rawIds.filter(Boolean);
        if (!mapKey || !ids) {
            console.error('model and id cannot be empty or null');
        }
        const [modelName, organizationID] = mapKey.split('~');
        const now = Date.now();
        // filter out if we already have the item and its been less than 5 seconds
        const idsAlreadyLoaded = ids.map((id) => {
            const key = `${organizationID}~${id}`;
            const localItem = self.getItem(modelName, key);
            // console.log('localItem', localItem);
            if (localItem && localItem.syncState === 'loaded' && (now - localItem.loadedAt) < DEFAULT_MAX_AGE * 1000) {
                // console.log('alredy loaded', {
                //   key,
                //   now,
                //   loadedAt: localItem.loadedAt,
                // });
                return id;
            }
            return null;
        }).filter(Boolean);
        const idsToLoad = difference(ids, idsAlreadyLoaded);
        // console.log(`Batch Loading: ${mapKey}`, idsToLoad);
        // call graphql to get the items if there any to load
        // otherwise just return the items we already have
        const batchPromise = idsToLoad.length > 0 ? batchLoadHelper(modelName, organizationID, idsToLoad, self.isPortalApi) : Promise.resolve([]);
        const loadPromise = batchPromise.then((remoteItems) => {
            const localItems = ids.map((id) => {
                const key = `${organizationID}~${id}`;
                const loadKey = `${modelName}:${key}`;
                const localItem = self.getItem(modelName, key) || self.stub(modelName, key, 'loading...');
                if (!localItem) {
                    console.log('No local item', loadKey);
                    console.trace();
                }
                // already have it and its already good, just return it
                if (idsAlreadyLoaded.includes(id)) {
                    return localItem;
                }
                // find new/updated result from server
                const remoteItem = remoteItems.find((ri) => (id === ri.id));
                if (remoteItem) {
                    schema.mapValuesFromGet(modelName, remoteItem, localItem);
                    localItem.resetLoadedAt();
                }
                else {
                    // not found so its a bad id
                    console.log('Item not found', loadKey);
                    localItem.setNotFound();
                }
                loadItemMap.delete(loadKey);
                // console.log(`${getTimestamp()} Removed: ${loadKey},
                // loadItemMap.size: ${loadItemMap.size}`);
                return localItem;
            });
            return localItems;
        });
        // add these items to the load map so calls to getOrLoad don't trigger
        // a seperate load
        // console.log('PUT IN LOAD MAP: ', ids);
        idsToLoad.forEach((id) => {
            const key = `${organizationID}~${id}`;
            const loadKey = `${modelName}:${key}`;
            if (loadItemMap.has(loadKey)) {
                // console.log(`${getTimestamp()} already loading: ${loadKey},
                // loadItemMap.size: ${loadItemMap.size}`);
            }
            else {
                const itemPromise = async () => {
                    const localItems = await loadPromise;
                    return localItems.find((item) => (item.key === key));
                };
                loadItemMap.set(loadKey, itemPromise());
                // console.log(`${getTimestamp()} Loading: ${loadKey},
                // loadItemMap.size: ${loadItemMap.size}`);
            }
        });
        const localItems = await loadPromise;
        // console.log('batchLoadModel returning', localItems);
        return localItems;
    },
    batchLoadFilter(organizationId, modelName, filter, sort, dependencies = [], itemsPerPage = 500) {
        return batchLoadWithDependencies(organizationId, modelName, filter, sort, dependencies, itemsPerPage);
    },
    loadCurrentOrganizations(orgs) {
        orgs.forEach((remoteItem) => {
            if (!remoteItem)
                return;
            const localItem = this.remoteItemToLocal('organization', remoteItem);
            self.currentOrganizations.push(localItem.key);
        });
    },
    loadCurrentUserOrganizations(userOrgs) {
        self.currentUserOrganizations = cast([]);
        userOrgs.forEach((remoteItem) => {
            if (!remoteItem)
                return;
            const localItem = this.remoteItemToLocal('userOrganization', remoteItem);
            self.currentUserOrganizations.push(localItem.key);
        });
    },
    addToCurrentOrganizations(organizationId) {
        if (!self.currentOrganizations.includes(organizationId)) {
            self.currentOrganizations.push(organizationId);
        }
    },
    addToCurrentUserOrganizations(userOrganizationId) {
        if (!self.currentUserOrganizations.includes(userOrganizationId)) {
            self.currentUserOrganizations.push(userOrganizationId);
        }
    },
    addStub: flow(function* add(modelName, stub, clearCache = true) {
        console.log(`Adding stubbed ${modelName}`, stub);
        console.log('self.getMapOf(modelName):', self.getMapOf(modelName));
        if (stub.preUpdate) {
            yield stub.preUpdate(true);
        }
        self.getMapOf(modelName).put(stub);
        if (isOrganizationScoped(modelName)) {
            stub.organization = self.currentOrganization;
            stub.organizationID = self.currentOrganization?.id;
            stub.createdAt = new Date();
            stub.updatedAt = stub.createdAt;
        }
        const input = schema.getValuesForUpdate(modelName, stub, self.isPortalApi, true);
        stub.syncState = 'saving';
        const query = schema.getCreateGQL(modelName, self.isPortalApi);
        console.log('Variables for update', { query, input });
        try {
            const result = yield getGQLAPI().graphql({
                query,
                variables: {
                    input,
                },
            });
            console.log('response for addStub', modelName, result);
            stub.syncState = 'loaded';
            stub.resetChange();
            self.getMapOf(modelName).put(stub);
            if (clearCache) {
                self.clearCachedList(modelName);
            }
            return result;
        }
        catch (error) {
            console.log(`Error adding ${modelName}`, error);
            throw Error('Error adding item');
            // airbrake.notify(error);
        }
    }),
    update: flow(updateFunction(self)),
    remove: flow(function* remove(modelName, item) {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        console.log(`Removing item ${item}`);
        self.state = 'pending';
        try {
            const variables = {
                input: schema.getPrimaryKeysForDelete(modelName, item),
            };
            if (isOrganizationScoped(modelName)) {
                variables.input.organizationID = self.currentOrganization.id;
            }
            yield getGQLAPI().graphql({
                query: schema.getDeleteGQL(modelName, self.isPortalApi),
                variables,
            });
            // need to remove references to it or there is an error.
            self.removeFromCachedList(modelName, item);
            // TODO: trap fail here
            destroy(item);
            self.state = 'done';
        }
        catch (error) {
            self.state = 'error';
            console.log(`Error removing ${modelName}`, error);
            // airbrake.notify(error);
        }
    }),
    clone(modelName, item) {
        if (item.organization?.id !== self.currentOrganization?.id) {
            console.error('Cannot clone item from another organization', { item, currentOrganization: self.currentOrganization });
            return null;
        }
        const newItem = self.create(modelName, `${self.currentOrganization?.id}~${v4()}`, `Copy of ${item.name}`, omit(cloneDeep(getSnapshot(item)), ['changeCount', 'syncState', 'createdAt', 'updatedAt', 'id', 'key', 'name']));
        return newItem;
    },
    getProgramProgress: flow(function* getProgramProgress(programID) {
        try {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            const variables = {
                programID,
                userId: { eq: self.currentUser?.id },
            };
            console.log('ssq:', variables);
            const result = yield getGQLAPI().graphql(graphqlOperation(programProgressByProgramIDCustomerID, variables));
            if (result.data.programProgressByProgramIDCustomerID.items.length) {
                const sectionProgress = JSON.parse(result.data.programProgressByProgramIDCustomerID.items[0].sectionProgress);
                let { quizResults } = result.data.programProgressByProgramIDCustomerID.items[0];
                if (quizResults !== null) {
                    quizResults = JSON.parse(quizResults);
                }
                const item = {
                    ...result.data.programProgressByProgramIDCustomerID.items[0],
                    sectionProgress,
                    quizResults,
                };
                console.log('programProgress', item);
                const localItem = self.stub('programProgress', item.id, 'loading...');
                schema.mapValuesFromGet('programProgress', item, localItem);
                localItem.resetLoadedAt();
                return localItem;
            }
        }
        catch (e) {
            console.log('Error', e);
        }
        return false;
    }),
    remoteItemToLocal(modelName, remoteItem) {
        const organizationID = remoteItem?.organizationID || self.currentOrganization?.id;
        const key = isOrganizationScoped(modelName) ? `${organizationID}~${remoteItem.id}` : remoteItem.id;
        let localItem = self.getItem(modelName, key);
        if (!localItem) {
            localItem = self.stub(modelName, key, 'loading...');
        }
        schema.mapValuesFromGet(modelName, remoteItem, localItem);
        localItem.resetLoadedAt();
        return localItem;
    },
}))
    .actions((self) => ({
    batchLoad: async (loadKeys) => {
        const byModel = loadKeys.filter(Boolean).reduce((modelMap, loadKey) => {
            if (!loadKey.modelName) {
                return modelMap;
            }
            const { organizationID, id } = splitKey(loadKey.key, self.currentOrganization?.id);
            const mapKey = `${loadKey.modelName}~${organizationID}`;
            modelMap[mapKey] = modelMap[mapKey] || [];
            modelMap[mapKey].push(id);
            return modelMap;
        }, {});
        const promises = Object.keys(byModel).map((mapKey) => (self.batchLoadModel(mapKey, byModel[mapKey])));
        const items = (await Promise.all(promises)).flat();
        // console.log('items', items);
        return items;
    },
    setCombinedLibraries(list) {
        self.combinedLibraries = list;
    },
    setReceivedLibraries(list) {
        self.receivedLibraries = list;
    },
    setUserRoles(roles) {
        self.currentUserRoles.replace(roles);
    },
}))
    .views((self) => ({
    getOrLoad(modelName, keyOrID, maxAge) {
        if (modelName === 'category') {
            // only exist as part of portal
            const category = self.currentPortal?.getCategory(keyOrID);
            console.log('Category is', category);
            return category;
        }
        if (modelName === 'hubCategory') {
            // only exist as part of portal
            const category = self.currentHub?.getCategory(keyOrID);
            console.log('Category is', category);
            return category;
        }
        const key = resolveKey(keyOrID, self.currentOrganization?.id, modelName);
        // see if we already have it
        let item = self.getItem(modelName, key);
        if (item) {
            // console.log('Found item in RootStore', key);
        }
        else {
            item = self.stub(modelName, key, 'loading...');
            // console.log('Stubbed item', key);
        }
        self.syncLoadingStateBeforeDeferredLoad(modelName, key, maxAge);
        setTimeout(() => self.load(modelName, key, maxAge), 0);
        return item;
    },
    getCachedList(modelName, filter = {}, sort = {}, crossOrganization = false) {
        const key = getListKey(self.currentOrganization?.id, modelName, filter, sort, crossOrganization);
        // console.log(`getCachedList called for ${key}`);
        const finalSort = { ...sort };
        if (modelName === 'invite') {
            finalSort.field = 'inviteeEmail';
        }
        // see if we already have it
        const list = self.cachedLists.get(key)
            || self.stubCachedList(modelName, filter, finalSort, crossOrganization);
        if (list.syncState === 'stubbed') {
            /*
              TODO: this is ugly, but workaround the idea that views should be side effect free.
            We need a more elegant solution..
            */
            // console.log(`Calling fetchCachedList on ${modelName}`);
            const canFetch = crossOrganization
                || self.isPortalApi
                || !list.isOrganizationScoped
                || !!self.currentOrganization?.id;
            if (canFetch) {
                setTimeout(() => self.fetchCachedList(list), 0);
            }
        }
        return list;
    },
    getBySlug: (modelName, slug) => {
        const cachedItem = self.slugCache.get(`${modelName}/${slug}`);
        if (cachedItem) {
            // console.log('Found cached item', cachedItem);
            return cachedItem;
        }
        return null;
    },
    cacheSlug: (modelName, slug, id) => {
        self.slugCache.set(`${modelName}/${slug}`, id);
        console.log(`Cached ${modelName}/${slug}`, id);
    },
    searchBySlug: async (modelName, slug) => {
        if (!slug?.length)
            return null;
        const cachedItem = self.getBySlug(modelName, slug);
        if (cachedItem) {
            console.log('searchBySlug found cached item', cachedItem);
            return cachedItem;
        }
        const parts = slug.split('~');
        let slugKey;
        if (parts.length === 1) {
            slugKey = slug;
        }
        else {
            // eslint-disable-next-line prefer-destructuring
            slugKey = parts[1];
        }
        if (modelName === 'category') {
            // only exist as part of portal
            const category = self.currentPortal?.getCategoryBySlug(slugKey);
            if (!category) {
                return null;
            }
            self.setSlug(modelName, slug, category.id);
            // console.log('searchBySlug Category is', category.id);
            return category.id;
        }
        if (modelName.startsWith('hub')) {
            // only exist as part of portal
            return slug;
        }
        const avoidSearchByLibrary = ['instructor'];
        const librariesToSearch = [self.currentPortal?.defaultLibrary?.id];
        self.currentPortal?.libraries.forEach((library) => {
            librariesToSearch.push(library.id);
        });
        self.currentPortal?.libraryShares.forEach((share) => {
            librariesToSearch.push(share.libraryID);
        });
        const modelNameSearch = modelName === 'class' ? 'course' : modelName;
        const organizationId = self.currentOrganization?.id ?? '';
        const searchResults = await loadBySlug(organizationId, modelNameSearch, slugKey, avoidSearchByLibrary.includes(modelName) ? [] : librariesToSearch, self.isPortalApi);
        if (searchResults && searchResults.length > 0) {
            const item = searchResults[0];
            const localItem = self.remoteItemToLocal(modelNameSearch, item);
            self.setSlug(modelName, slug, localItem.key);
            return localItem.key;
        }
        return null;
    },
    // @todo: probably dont want this - should use cached list instead
    allItems(modelName, filter) {
        const map = self.getMapOf(modelName);
        const list = Array.from(map.values());
        return filter ? list.filter(filter) : list;
    },
    getItemProgress(item) {
        console.log('getItemProgress:', item.key);
        if (!item) {
            console.log('missing item');
            return undefined;
        }
        if (item && !item.isCourse) {
            console.log('unexpected value for item', getSnapshot(item));
            return undefined;
        }
        const link = self.getProgressLink(item.key);
        return link?.progress;
        // if (item.isCourse()) {
        //   return self.currentCourseProgresses.find((progress) => (item === progress.course));
        // }
        // if (item.isMeeting()) {
        //   return self.currentMeetingRegistrations.find((progress) => (item === progress.meeting));
        // }
        // if (item.isProgram()) {
        //   return self.currentProgramProgresses.find((progress) => (item === progress.program));
        // }
        // if (item.isMicrocast()) {
        //   // console.log('cc:', self.currentMicrocastProgresses,
        //   // self.currentMicrocastProgresses.find((progress) => (item === progress.microcast)));
        //   return self.currentMicrocastProgresses.find((progress) => (item === progress.microcast));
        // }
        // console.log('No progress for item', item?.name);
        // return undefined;
        // // also returns null if we dont have any progress for that item.
    },
    combinedLibraryIDs() {
        // this is the IDs of the libraries
        return self.combinedLibraries.map((item) => (item.getLibraryID()));
    },
    receivedLibraryShareKeys() {
        // this is the IDs of the libraryShares
        return self.receivedLibraries.map((item) => (item.key));
    },
}))
    .actions((self) => {
    const pollingMap = new Map();
    const startPollingModel = (modelName, key, interval = 3000, callback) => {
        const pollingKey = `${modelName}-${key}`;
        if (pollingMap.has(pollingKey)) {
            // Already polling this item
            return;
        }
        const throttledPoll = throttle(async () => {
            try {
                const item = await self.load(modelName, key, 1);
                if (callback) {
                    callback(item);
                }
            }
            catch (error) {
                console.error(`Error polling ${modelName} ${key}:`, error);
            }
        }, interval, { leading: true, trailing: false });
        // Start polling immediately
        throttledPoll();
        const intervalId = setInterval(throttledPoll, interval);
        pollingMap.set(pollingKey, intervalId);
    };
    const stopPollingModel = (modelName, key) => {
        const pollingKey = `${modelName}-${key}`;
        const intervalId = pollingMap.get(pollingKey);
        if (intervalId) {
            clearInterval(intervalId);
            pollingMap.delete(pollingKey);
        }
    };
    return {
        startPollingModel,
        stopPollingModel,
    };
})
    .actions((self) => {
    const updateProgramProgress = (programProgressesResponse) => {
        const programProgresses = programProgressesResponse?.map((progressJSON) => (self.remoteItemToLocal('programProgress', progressJSON)));
        // console.log('programProgresses:', programProgresses);
        self.addCurrentProgramProgresses(programProgresses);
    };
    return {
        add: flow(function* add(modelName, values) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log(`Adding ${modelName}`, values);
            if (isOrganizationScoped(modelName) && !values.organization) {
                values.organization = self.currentOrganization.id;
            }
            const cappedName = schema.getCapped(modelName);
            const input = schema.getValuesForUpdate(modelName, values, self.isPortalApi, true);
            const query = schema.getCreateGQL(modelName, self.isPortalApi);
            // console.log('Variables for update', input);
            try {
                const result = yield getGQLAPI().graphql({
                    query,
                    variables: {
                        input,
                    },
                });
                console.log('new model', result);
                const remoteItem = result.data[`create${cappedName}`];
                const localItem = self.stub(modelName, resolveKey(remoteItem.id, self.currentOrganization?.id, modelName), remoteItem.name);
                const newItem = schema.mapValuesFromGet(modelName, remoteItem, localItem);
                self.getCachedList(modelName).items.push(newItem);
                return newItem;
            }
            catch (error) {
                console.log(`Error adding ${modelName}`, error);
                // airbrake.notify(error);
            }
            return null;
        }),
        setCurrentOrganization: async (org) => {
            // console.log(`${getTimestamp()} Set current org`, org.key);
            self.currentLibrary = undefined;
            self.currentOrganization = org;
            self.currentPortal = undefined;
            self.currentRepository = undefined;
            self.clearCachedLists();
            self.libraries.clear();
            self.libraryShares.clear();
            self.repositories.clear();
            self.portals.clear();
            self.programs.clear();
            self.courses.clear();
            self.combinedLibraries.clear();
            self.receivedLibraries.clear();
            // self.getCachedList('portal');
            // self.getCachedList('course');
            if (!self.isPortalApi) {
                const repositoryList = self.getCachedList('repository');
                repositoryList.setRequestedCount(1000);
                const libraryList = self.getCachedList('library');
                libraryList.setRequestedCount(1000);
                const libraryShareList = self.getCachedList('libraryShare', {
                    targetOrganizationID: { eq: self.currentOrganization?.id },
                }, { field: 'name', direction: 'asc' }, true);
                libraryShareList.setRequestedCount(1000);
                await self.fetchCachedList(libraryList);
                await self.fetchCachedList(libraryShareList);
                self.setCombinedLibraries([...libraryList.getItems(), ...libraryShareList.getItems()]);
                self.setReceivedLibraries([...libraryShareList.getItems()]);
                // console.log('combinedLibraries', self.combinedLibraries);
                // remember it for later if in browser
                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('currentOrganizationID', org.id);
                }
                // console.log('links', getSnapshot(self.currentUserOrganizations));
                const link = self.currentUserOrganizations.find((orgLink) => (orgLink.organization.id === org.id));
                let userRoles = [];
                if (link?.superUser) {
                    userRoles = ['SUPERUSER'];
                }
                else {
                    userRoles.push(...(link?.roles || []));
                    if (userRoles.length === 0) {
                        userRoles.push('FULLADMIN');
                    }
                }
                self.setUserRoles(userRoles);
                // update customer to reflect new the organization
                if (self.currentCustomer) {
                    self.setCurrentCustomer({
                        ...self.currentCustomer,
                        organizationID: org.id,
                    });
                }
            }
        },
        startCourse: (course) => {
            const loadKey = `startCourse:${course.key}`;
            let loadPromise = loadItemMap.get(loadKey);
            if (loadPromise) {
                console.log('Returning existing promise', loadKey);
                return loadPromise;
            }
            const doStart = async () => {
                try {
                    if (!self.currentOrganization) {
                        throw Error('currentOrganization cannot be empty');
                    }
                    if (!self.currentPortal) {
                        throw Error('currentPortal cannot be empty');
                    }
                    // if anonymous and either public portal or course allows anonymous access
                    if (self.isAnonymous && (self.currentPortal.type === 'public'
                        || course.accessConfiguration === 'anonymous')) {
                        return self.stub('courseProgress', v4(), 'Anonymous course progress');
                    }
                    const variables = {
                        courseKey: course.key,
                        portalID: self.currentPortal.id,
                        ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization.id }),
                    };
                    const gql = startCourseGQL(self.isPortalApi);
                    console.log('startCourse request', { gql, variables });
                    const result = await getGQLAPI().graphql(graphqlOperation(gql, variables));
                    console.log('startCourse result', result);
                    const { statusCode, message } = result.data.startCourse;
                    if (statusCode !== 200) {
                        const errorDetails = {
                            statusCode,
                            message: message || 'Unknown error occurred',
                        };
                        throw errorDetails;
                    }
                    const programProgresses = result.data.startCourse.programProgresses?.map((p) => (self.remoteItemToLocal('programProgress', p))) || [];
                    self.addCurrentProgramProgresses(programProgresses);
                    if (result.data.startCourse.courseProgress) {
                        const localItem = self.remoteItemToLocal('courseProgress', result.data.startCourse.courseProgress);
                        self.addCurrentCourseProgress(localItem);
                        return localItem;
                    }
                }
                catch (e) {
                    if (e?.statusCode) {
                        throw e;
                    }
                    console.log('Error', e);
                }
                return undefined;
            };
            loadPromise = doStart();
            loadItemMap.set(loadKey, loadPromise);
            return loadPromise;
        },
        startDocument: (document) => {
            const loadKey = `startDocument:${document.key}`;
            let loadPromise = loadItemMap.get(loadKey);
            if (loadPromise) {
                console.log('Returning existing promise', loadKey);
                return loadPromise;
            }
            const doStart = async () => {
                try {
                    if (!self.currentOrganization) {
                        throw Error('currentOrganazation cannot be empty');
                    }
                    if (!self.currentPortal) {
                        throw Error('currentPortal cannot be empty');
                    }
                    // if anonymous and either public portal or document allows anonymous access
                    if (self.isAnonymous && (self.currentPortal.type === 'public'
                        || document.accessConfiguration === 'anonymous')) {
                        return self.stub('documentProgress', v4(), 'Anonymous document progress');
                    }
                    const variables = {
                        documentKey: document.key,
                        portalID: self.currentPortal.id,
                        ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization.id }),
                    };
                    const gql = startDocumentGQL(self.isPortalApi);
                    console.log('startDocument request', { gql, variables });
                    const result = await getGQLAPI().graphql(graphqlOperation(gql, variables));
                    console.log('startDocument result', result);
                    const startDocumentResult = result.data.startDocument;
                    const remoteProgress = startDocumentResult.documentProgress;
                    const localItem = self.remoteItemToLocal('documentProgress', remoteProgress);
                    self.addCurrentDocumentProgress(localItem);
                    return localItem;
                }
                catch (e) {
                    console.log('Error', e);
                }
                finally {
                    loadItemMap.delete(loadKey);
                }
                return undefined;
            };
            loadPromise = doStart();
            loadItemMap.set(loadKey, loadPromise);
            return loadPromise;
        },
        startMicrocast: (microcast) => {
            const loadKey = `startMicrocast:${microcast.key}`;
            let loadPromise = loadItemMap.get(loadKey);
            if (loadPromise) {
                console.log('Returning existing promise', loadKey);
                return loadPromise;
            }
            const doStart = async () => {
                try {
                    if (!self.currentOrganization) {
                        throw Error('currentOrganazation cannot be empty');
                    }
                    if (!self.currentPortal) {
                        throw Error('currentPortal cannot be empty');
                    }
                    if (self.isAnonymous && (self.currentPortal.type === 'public'
                        || microcast.accessConfiguration === 'anonymous')) {
                        return self.stub('microcastProgress', v4(), 'Anonymous microcast progress');
                    }
                    const variables = {
                        microcastKey: microcast.key,
                        portalID: self.currentPortal.id,
                        ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization.id }),
                    };
                    const query = startMicrocastGQL(self.isPortalApi);
                    console.log('startMicrocast request', { query, variables });
                    const result = await getGQLAPI().graphql({
                        query,
                        variables,
                        // authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
                    });
                    console.log('startMicrocast result', result);
                    const startMicrocastResult = result.data.startMicrocast;
                    if (startMicrocastResult.programProgresses) {
                        const programProgresses = startMicrocastResult.programProgresses?.map((p) => (self.remoteItemToLocal('programProgress', p))) || [];
                        self.addCurrentProgramProgresses(programProgresses);
                    }
                    if (startMicrocastResult.microcastProgress) {
                        const localItem = self.remoteItemToLocal('microcastProgress', startMicrocastResult.microcastProgress);
                        self.addCurrentMicrocastProgress(localItem);
                        return localItem;
                    }
                    return undefined;
                }
                catch (e) {
                    console.log('Error', e);
                }
                finally {
                    loadItemMap.delete(loadKey);
                }
                return undefined;
            };
            loadPromise = doStart();
            loadItemMap.set(loadKey, loadPromise);
            return loadPromise;
        },
        startProgram: flow(function* startProgram(programKey) {
            try {
                if (!self.currentOrganization) {
                    throw Error('currentOrganazation cannot be empty');
                }
                const variables = {
                    programKey,
                    portalID: self.currentPortal?.id,
                    ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization.id }),
                };
                const gql = startProgramGQL(self.isPortalApi);
                console.log('startProgram request', { gql, variables });
                const result = yield getGQLAPI().graphql(graphqlOperation(gql, variables));
                console.log('startProgramGQL result', result);
                if (result.data.startProgram.programProgress) {
                    const progress = result.data.startProgram.programProgress;
                    const localItem = self.remoteItemToLocal('programProgress', progress);
                    self.addCurrentProgramProgresses([localItem]);
                    return localItem;
                }
            }
            catch (e) {
                console.log('Error', e);
            }
            return undefined;
        }),
        trackCourseItemProgress: flow(function* trackCourseItemProgress(courseKey, courseItemID, progress, lastPosition, maxPosition) {
            if (!self.isPortalApi && !self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            // console.log('trackProgress called');
            const response = yield gqlCall(trackCourseItemProgressGQL(self.isPortalApi), {
                courseKey,
                courseItemID,
                organizationID: self.currentOrganization?.id,
                progress,
                lastPosition: Math.floor(lastPosition),
                maxPosition: Math.floor(maxPosition),
            });
            // console.log('Response', response);
            if (response?.data?.trackCourseItemProgress?.statusCode === 200) {
                const results = response.data.trackCourseItemProgress;
                const localItem = self.remoteItemToLocal('courseProgress', results.courseProgress);
                self.addCurrentCourseProgress(localItem);
                updateProgramProgress(results.programProgresses);
                // console.log('Updated course progress', localItem);
                return localItem;
            }
            return null;
        }),
        scormCommit: flow(function* scormCommit(itemType, itemKey, scormData) {
            if (!self.isPortalApi && !self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('scormCommit called', {
                itemKey,
                itemType,
                scormData,
            });
            const response = yield gqlCall(scormCommitGQL(self.isPortalApi), {
                commitData: {
                    itemKey,
                    itemType,
                    scormData: JSON.stringify(scormData),
                },
                ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization?.id }),
            });
            // TODO: update the RootModel's version of CourseProgress
            console.log('Response', response);
            if (response?.data?.scormCommit?.statusCode === 200) {
                const results = response.data.scormCommit;
                const localItem = itemType === 'course'
                    ? self.remoteItemToLocal('courseProgress', results.courseProgress)
                    : self.remoteItemToLocal('microcastProgress', results.microcastProgress);
                console.log('commit success', localItem);
                updateProgramProgress(results.programProgresses);
                return localItem;
            }
            return false;
        }),
        enrollCustomers: flow(function* enrollCustomersFunction(data) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('enrollCustomers called');
            const resp = yield gqlCall(enrollCustomersGQL, {
                ...data,
                organizationID: self.currentOrganization.id,
                portalID: self.currentPortal?.id,
            });
            if (resp?.data?.enrollCustomers.includes('statusCode=200')) {
                return 'SUCCESS';
            }
            return 'FAILED';
        }),
        getCustomersWithProgress: flow(function* getCustomersWithProgressFunction(data) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('getCustomersWithProgress called');
            const resp = yield gqlCall(getCustomersWithProgressGQL, data);
            if (resp?.data?.getCustomersWithProgress) {
                const result = resp.data.getCustomersWithProgress;
                console.log('getCustomersWithProgress API result', result);
                if (result.statusCode === 200) {
                    return result.body;
                }
            }
            return [];
        }),
        trackProgramProgress: flow(function* trackProgramProgressFunction(programKey, programSectionID, progress) {
            console.log('trackProgress called');
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            const resp = yield gqlCall(trackProgramProgressGQL(self.isPortalApi), {
                programKey,
                programItemID: programSectionID, // todo fix this so its section id in the api
                progress,
                organizationID: self.currentOrganization.id,
            });
            if (resp?.data?.trackProgramProgress?.statusCode === 200) {
                const localItem = self.remoteItemToLocal('programProgress', resp.data.trackProgramProgress.programProgress);
                console.log('Updated programProgress', localItem);
                self.addCurrentProgramProgresses([localItem]);
                return localItem;
            }
            return resp;
        }),
        trackMicrocastProgress: flow(function* trackMicrocastProgressFunction(microcastKey, progress, lastPosition, maxPosition) {
            console.log('trackMicrocastProgress called');
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            const response = yield gqlCall(trackMicrocastProgressGQL(self.isPortalApi), {
                microcastKey,
                progress,
                organizationID: self.currentOrganization.id,
                lastPosition: Math.floor(lastPosition),
                maxPosition: Math.floor(maxPosition),
            });
            if (response?.data?.trackMicrocastProgress?.statusCode === 200) {
                const results = response.data.trackMicrocastProgress;
                const localItem = self.remoteItemToLocal('microcastProgress', results.microcastProgress);
                self.addCurrentMicrocastProgress(localItem);
                updateProgramProgress(results.programProgresses);
                console.log('Updated microcast progress', localItem);
                return localItem;
            }
            return null;
        }),
        registerForMeeting: flow(function* registerForMeeting(meetingKey) {
            if (!self.currentOrganization) {
                throw Error('currentOrganization cannot be empty');
            }
            if (!self.currentPortal) {
                throw Error('currentPortal cannot be empty');
            }
            const loadKey = `registerMeeting:${meetingKey}`;
            // Check if registration is in progress
            const loadInProgress = loadItemMap.get(loadKey);
            if (loadInProgress) {
                console.log(`Yield to existing registration for: ${loadKey}`);
                const item = yield loadInProgress;
                return item;
            }
            const variables = {
                meetingKey,
                portalID: self.currentPortal.id,
                ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization.id }),
            };
            const gql = registerForMeetingGQL(self.isPortalApi);
            console.log('registerForMeeting request', { gql, variables });
            try {
                // Create a registration request and add it to the map
                const registrationRequest = getGQLAPI().graphql(graphqlOperation(gql, variables));
                loadItemMap.set(loadKey, registrationRequest);
                // Await the registration result
                const result = yield registrationRequest;
                console.log('registerForMeeting result', result);
                if (result.data.registerForMeeting.Registration) {
                    const localItem = self.remoteItemToLocal('meetingRegistration', result.data.registerForMeeting.Registration);
                    self.setCurrentMeetingRegistration(localItem);
                    return localItem;
                }
                const errorMessage = result?.data?.registerForMeeting?.message || 'Not authorized to access this meeting.';
                throw Error(errorMessage);
            }
            catch (error) {
                console.log('Error in registerForMeeting:', error);
                throw error;
            }
            finally {
                loadItemMap.delete(loadKey); // Ensure cleanup
            }
        }),
        getMeetingRegistration: flow(function* getMeetingRegistration(meetingKey) {
            console.log('getMeetingRegistration called');
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            if (!self.currentPortal) {
                throw Error('currentPortal cannot be empty');
            }
            const loadKey = `meetingRegistration:${meetingKey}`;
            // Check if loading in progress
            const loadInProgress = loadItemMap.get(loadKey);
            if (loadInProgress) {
                console.log(`Yield to existing load for: ${loadKey}`);
                const item = yield loadInProgress;
                return item;
            }
            try {
                const loadRequest = gqlCall(getMeetingRegistrationForCustomerGQL(self.isPortalApi), {
                    organizationID: self.currentOrganization.id,
                    portalID: self.currentPortal?.id,
                    meetingKey,
                });
                loadItemMap.set(loadKey, loadRequest);
                const resp = yield loadRequest;
                if (resp?.data?.getMeetingRegistrationForCustomer?.Registration) {
                    const localItem = self.remoteItemToLocal('meetingRegistration', resp.data.getMeetingRegistrationForCustomer?.Registration);
                    self.setCurrentMeetingRegistration(localItem);
                    self.addCurrentMeetingProgress(localItem);
                    console.log('Returned meetingRegistration', localItem);
                    return localItem;
                }
                return null;
            }
            catch (e) {
                console.log('Error', e);
                self.setCurrentMeetingRegistration(undefined);
                throw e;
            }
            finally {
                loadItemMap.delete(loadKey);
            }
        }),
        getMeetingAttendeeInfo: flow(function* getMeetingAttendeeInfo(meetingKey, attendeeID) {
            console.log('getMeetingAttendeeInfo called');
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            if (!self.currentPortal) {
                throw Error('currentPortal cannot be empty');
            }
            try {
                const resp = yield gqlCall(getMeetingAttendeeInfoGQL(self.isPortalApi), {
                    organizationID: self.currentOrganization.id,
                    portalID: self.currentPortal?.id,
                    meetingKey,
                    attendeeID,
                });
                return resp?.data?.getMeetingAttendeeInfo;
            }
            catch (e) {
                console.log('Error', e);
                return null;
            }
        }),
        sendMeetingInvites: flow(function* sendMeetingInvites(meetingKey, subject, message, emailAddresses) {
            if (!self.currentOrganization) {
                throw Error('currentOrganization cannot be empty');
            }
            if (!self.currentPortal) {
                throw Error('currentPortal cannot be empty');
            }
            try {
                const request = {
                    query: sendMeetingInvitesGQL(self.isPortalApi),
                    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                    variables: {
                        organizationID: self.currentOrganization?.id,
                        portalID: self.currentPortal?.id,
                        meetingKey,
                        subject,
                        message,
                        emailAddresses,
                    },
                };
                console.log('sendMeetingInvites request', request);
                const result = yield getGQLAPI().graphql(request);
                console.log('sendMeetingInvites result', result);
                if (!result?.data?.sendMeetingInvites) {
                    throw new Error('Failed to send meeting invites.');
                }
            }
            catch (error) {
                console.error('Error sending meeting invites', error);
                throw error;
            }
        }),
        executeEngagementFlow: flow(function* executeEngagementFlowFunction(data) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('executeEngagementFlow called');
            const resp = yield executeEngagementFlowAction({
                ...data,
                organizationID: self.currentOrganization.id,
            });
            return resp;
        }),
        updateQuizResults: flow(function* updateQuizResultsFunction(data) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('updateQuizResults called');
            const response = yield gqlCall(updateQuizResults, data);
            if (response?.data?.updateQuizResults?.statusCode === 200) {
                if (response.data.updateQuizResults.programProgress) {
                    let progress = null;
                    const p = response.data.updateQuizResults.programProgress;
                    if (p.sectionProgress) {
                        let parsedSP = JSON.parse(p.sectionProgress);
                        if (typeof parsedSP !== 'object') {
                            parsedSP = JSON.parse(parsedSP);
                        }
                        progress = {
                            ...p,
                            sectionProgress: parsedSP,
                        };
                    }
                    const localItem = self.stub('programProgress', progress.id, 'loading...');
                    schema.mapValuesFromGet('programProgress', progress, localItem);
                    localItem.resetLoadedAt();
                    self.addCurrentProgramProgresses([progress]);
                    return localItem;
                }
                if (response.data.updateQuizResults.courseProgress) {
                    const progress = response.data.updateQuizResults.courseProgress;
                    const localItem = self.stub('courseProgress', progress.id, 'loading...');
                    schema.mapValuesFromGet('courseProgress', progress, localItem);
                    localItem.resetLoadedAt();
                    self.addCurrentCourseProgress(localItem);
                    return localItem;
                }
            }
            return null;
        }),
        updateStripeCredentials: flow(function* updateStripeCredentialsFunction(publicKey, secretKey) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            console.log('updateOrganizationPaymentCreds called');
            const response = yield gqlCall(updateOrganizationPaymentCreds, {
                organizationID: self.currentOrganization.id,
                publicKey,
                secretKey,
            });
            if (response?.data?.updateOrganizationPaymentCreds?.statusCode === 200) {
                const { organization } = response.data.updateOrganizationPaymentCreds;
                const organizationID = organization.id;
                let localItem = self.getItem('organization', organizationID);
                if (!localItem) {
                    localItem = self.stub('organization', organizationID, 'loading');
                }
                schema.mapValuesFromGet('organization', organization, localItem);
                localItem.resetLoadedAt();
                console.log('Updated organization', localItem);
                console.log('AJ API', response.data.updateOrganizationPaymentCreds);
            }
            return null;
        }),
        updateGoogleApiCredentials: flow(function* updateGoogleApiCredentials(newCredentials, whenDone) {
            if (!self.currentOrganization) {
                throw Error('currentOrganization cannot be empty');
            }
            console.log('updateOrganization called');
            self.currentOrganization.updateValues({
                googleApiConfig: newCredentials,
            });
            const resp = yield* (updateFunction(self)('organization', self.currentOrganization));
            if (whenDone) {
                whenDone();
            }
            return resp;
        }),
        saveAccessGroups: flow(function* updateAccessGroups(newAccessGroups, whenDone) {
            if (!self.currentOrganization) {
                throw Error('currentOrganazation cannot be empty');
            }
            // self.currentOrganization.updateAccessGroups(newAccessGroups);
            console.log('updateOrganization called');
            self.currentOrganization.updateValues({
                accessGroups: newAccessGroups,
            });
            const resp = yield* (updateFunction(self)('organization', self.currentOrganization));
            const resp2 = yield syncOrganizationToCognitoAction({
                organizationID: self.currentOrganization.id,
            });
            whenDone();
            return { resp, resp2 };
        }),
        getCustomerInfo: flow(function* getCustomerInfo(portalApi) {
            const request = {
                query: customerInfoGQL(portalApi),
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                variables: {
                    organizationID: self.currentOrganization?.id,
                    portalID: self.currentPortal?.id,
                },
            };
            // console.log('getCustomerInfo request', request);
            const result = yield getGQLAPI().graphql(request);
            const info = result.data?.customerInfo;
            console.log(`${getTimestamp()} customerInfo result`, result);
            // await portalStore.load('customer', info?.Internal?.id);
            if (info) {
                // const email = info.UserAttributes?.find((attr) => (attr?.Name === 'email'));
                // console.log('Email', email?.Value);
                if (info.Internal?.id) {
                    self.setCurrentAccess(info.Access);
                    self.setCurrentMembers((info.Members).map((member) => (member.id)));
                    self.setCurrentCustomer(info.Internal);
                    // @todo just put these into customer directly
                    self.setCurrentCourseProgresses(info.CourseProgresses);
                    self.setCurrentMeetingRegistrations(info.MeetingRegistrations);
                    self.setCurrentMicrocastProgresses(info.MicrocastProgresses);
                    const programProgresses = [];
                    info.ProgramProgresses.forEach((p) => {
                        if (p.sectionProgress) {
                            let parsedSP = JSON.parse(p.sectionProgress);
                            if (typeof parsedSP !== 'object') {
                                parsedSP = JSON.parse(parsedSP);
                            }
                            programProgresses.push({
                                ...p,
                                sectionProgress: parsedSP,
                            });
                        }
                    });
                    self.setCurrentProgramProgresses(programProgresses);
                    self.isAnonymous = false;
                }
                else {
                    console.log('missing customer internal');
                }
            }
            else {
                console.log('No customer returned');
                self.isAnonymous = true;
            }
        }),
        logoutCustomer: flow(function* logoutCustomer() {
            yield Auth.signOut();
            self.currentAccess = undefined;
            self.currentCustomer = undefined;
            self.currentCourseProgresses = cast([]);
            self.currentHub = undefined;
            self.currentLearningBoard = undefined;
            self.currentMeetingRegistrations = cast([]);
            self.currentMicrocastProgresses = cast([]);
            self.currentProgramProgresses = cast([]);
            self.isAnonymous = true;
            return true;
        }),
        getLearningBoard: flow(function* getLearningBoard(portal, customer) {
            const request = {
                query: getLearningBoardGQL(self.isPortalApi),
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                variables: {
                    organizationID: self.currentOrganization?.id,
                    portalID: portal.id,
                },
            };
            // console.log('getLearningBoard request', request);
            const result = yield getGQLAPI().graphql(request);
            const remote = result.data.learningBoard;
            const modelName = 'learningBoard';
            console.log(result);
            if (!remote) {
                console.log('no learning boards found');
                return null;
            }
            let local = self.getItem(modelName, remote.id);
            if (!local) {
                local = self.stub(modelName, remote.id, 'loading...');
            }
            schema.mapValuesFromGet(modelName, remote, local);
            local.syncState = 'loaded';
            local.resetLoadedAt();
            if (self.currentPortal?.id === portal.id && self.currentCustomer?.id === customer.id) {
                self.currentLearningBoard = local;
            }
            return local;
        }),
        listQuestionnairesByEmail: flow(function* listQuestionnairesByEmail(email) {
            if (!self.currentPortal) {
                console.log('No current portal');
                return [];
            }
            const request = {
                query: listQuestionairesByEmailGQL(self.isPortalApi),
                // authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                variables: {
                    organizationID: self.currentOrganization?.id,
                    portalIDEmailStatus: {
                        beginsWith: {
                            portalID: self.currentPortal.id,
                            email,
                        },
                    },
                },
            };
            console.log('questionnaires request', request);
            const modelName = 'questionnaire';
            const result = yield getGQLAPI().graphql(request);
            const remoteItems = result.data?.listQuestionairesByEmail?.items;
            if (!remoteItems) {
                return [];
            }
            try {
                return remoteItems.map((remoteItem) => (self.remoteItemToLocal(modelName, remoteItem)));
            }
            catch (error) {
                console.log('Error loading questionnaires', error);
                self.state = 'error';
            }
            return [];
        }),
        refreshCustomerStats: flow(function* refreshCustomerStats(customerID) {
            const query = refreshCustomerStatsGql;
            const variables = {
                organizationID: self.currentOrganization?.id,
                customerID,
            };
            try {
                const result = yield getGQLAPI().graphql({
                    query,
                    variables,
                });
                const remoteItem = result.data.refreshCustomerStats;
                const key = `${remoteItem.organizationID}~${remoteItem.id}`;
                let localItem = self.getItem('customer', key);
                if (!localItem) {
                    localItem = self.stub('customer', key, 'loading...');
                }
                schema.mapValuesFromGet('customer', remoteItem, localItem);
                console.log('updated customer', localItem);
            }
            catch (error) {
                console.log('Error refreshing customer stats', {
                    query,
                    error,
                    variables,
                });
                // airbrake.notify(error);
                navStore.errorDialog.showError('Error refreshing data', 'There was an error refreshing membership stats for the customer.');
            }
            return true;
        }),
        /**
         * Upgrades or downgrade (or otherwise) a membership by
         * creating a new member record with new membership
         * and expiring the existing one.
         */
        swapMembership: flow(function* swapMembership(existingMember, newMembership) {
            const query = swapMembershipGQL(self.isPortalApi);
            const variables = {
                organizationID: self.currentOrganization?.id,
                portalID: self.currentPortal?.id,
                existingMemberID: existingMember.id,
                newMembershipID: newMembership.id,
            };
            const request = {
                query,
                variables,
            };
            console.log('swap request', request);
            navStore.messageDialog.showConfirm('Updating memberships', 'Please wait while the existing membership is replaced with the new membership.');
            try {
                const result = yield getGQLAPI().graphql(request);
                console.log('Swap result', result);
                // const expiredMember = self.remoteItemToLocal('member', result.data.expiredMember);
                // const newMember = self.remoteItemToLocal('member', result.data.newMember);
                // console.log('swapped member', { expiredMember, newMember });
                self.clearCachedList('member');
                navStore.hideMessageDialog();
            }
            catch (error) {
                console.log('Error swapping member', {
                    query,
                    error,
                    variables,
                });
                // airbrake.notify(error);
                navStore.hideMessageDialog();
                navStore.errorDialog.showError('Error swapping membership', 'There was an error swapping membership for the customer.');
            }
            return true;
        }),
    };
})
    .actions((self) => ({
    refreshCognito: flow(function* refreshCognito() {
        // force reload of cognito groups
        if (self.isPortalApi) {
            console.log('Forcing update of cognito groups');
            try {
                const cognitoUser = yield Auth.currentAuthenticatedUser();
                const currentSession = yield Auth.currentSession();
                cognitoUser.refreshSession(currentSession.refreshToken, (err, session) => {
                    console.log('session', err, session);
                });
            }
            catch (e) {
                console.log('Unable to refresh Token', e);
            }
            yield self.getCustomerInfo(self.isPortalApi);
        }
        else {
            console.log('skipping cognitor refresh for admin user');
            yield self.getCustomerInfo(self.isPortalApi);
        }
    }),
    documentFromAsset: flow(function* documentFromAsset(asset) {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        // strip extension from filename
        const name = asset.name.replace(/\.[^/.]+$/, '');
        const document = yield self.add('document', {
            id: v4(),
            name,
            organization: self.currentOrganization,
            libraries: [self.currentLibrary],
            // image: asset,
            asset,
            publishedAt: asset.createdAt,
        });
        self.clearCachedList('document');
        console.log('Created document', document);
        navStore.showSnackBar(`Created document: ${document.name}`);
        return asset;
    }),
    addMember: (newMember) => {
        self.currentMembers.push(newMember.key);
    },
}))
    .actions((self) => ({
    createCustomer: flow(function* createCustomer(customer) {
        const request = {
            query: createCognitoCustomer,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                email: customer.email,
                emailVerified: true,
                temporaryPassword: customer.temporaryPassword,
            },
        };
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        const response = result.data.createCognitoCustomer;
        if (response.statusCode !== 200) {
            return Promise.reject(new Error(response?.message));
        }
        const { cognitoCustomer } = response;
        const newCustomer = yield self.add('customer', {
            ...customer,
            id: `${self.currentOrganization?.cognitoUserPoolID}_${cognitoCustomer.Username}`,
            temporaryPassword: '',
            members: [],
            learningBoard: [],
        });
        if (!newCustomer) {
            throw new Error('createCustomer: add returned null (see prior log)');
        }
        // Mirror update(): clear the source stub's change tracking so the editor's
        // usePrompt doesn't fire a "leave without saving" dialog after a successful create.
        // The new MST instance returned by add() is already clean.
        if (typeof customer?.resetChange === 'function')
            customer.resetChange();
        return newCustomer;
    }),
    addUserToOrganization: flow(function* addUserToOrganization(email, roles) {
        const request = {
            query: addUserToOrganizationGQL,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                email,
                roles,
            },
        };
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        const response = result.data.addUserToOrganization;
        if (response.statusCode !== 200) {
            throw Error(result.message);
        }
        // succeeded
        const { user: remoteUser, userOrganization: remoteUserOrg } = response;
        self.remoteItemToLocal('user', remoteUser);
        const userOrganization = self.remoteItemToLocal('userOrganization', remoteUserOrg);
        return userOrganization;
    }),
    resetPassword: flow(function* resetPassword(userId, password, forceChange) {
        const request = {
            query: resetPasswordGQL,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                userId,
                password,
                forceChange,
            },
        };
        const result = yield getGQLAPI().graphql(request);
        const response = result.data?.resetPassword;
        if (response.statusCode !== 200) {
            return {
                status: false,
                message: response.message,
            };
        }
        return {
            status: true,
            message: 'The password has been updated.',
        };
    }),
    upload: flow(function* upload(file, repository, setAsset, progressCallback, publicAccess, tags, pathPrefix) {
        if (!self.currentOrganization) {
            throw Error('currentOrganazation cannot be empty');
        }
        const length = yield processVideoDuration(file);
        console.log(`Uploading file: ${file} to repository ${repository}`);
        self.state = 'pending';
        const id = v4();
        const type = getAssetTypeFromFile(file);
        const assetSnap = {
            id,
            name: file.name,
            type,
            mimeType: file.type,
            status: 'defined',
            repository,
            length,
            ...(self.isAnonymous ? { tags } : {}),
            access: publicAccess ? 'public' : 'normal',
            path: `${pathPrefix ? `${pathPrefix}/` : ''}${id}`,
        };
        let asset = yield self.add('asset', assetSnap);
        setAsset(asset);
        console.log(`Uploading to bucket ${repository.bucket} with key ${asset.path}/original`, asset);
        const uploadResult = yield putObject(repository.bucket, `${asset.path}/original`, file, progressCallback);
        console.log('Finished upload', uploadResult);
        progressCallback(100, asset); // make sure we mark it as complete
        try {
            // triggers the processing and sets status to 'uploaded'
            asset = yield self.processAsset(asset);
            setAsset(asset);
            if (asset.status === 'processed') {
                navStore.showSnackBar(`Processing complete: ${asset.name}`);
                console.log('Completed upload and processing', asset);
            }
            else {
                console.log('Do polling for status');
                self.addItemToPoll('asset', asset.key, asset.id, asset.status, asset.updatedAt, (result) => {
                    console.log('Im updated', result);
                });
            }
            return asset;
        }
        catch (e) {
            console.log('Error on upload', e);
        }
        return asset;
    }),
    setCustomerAttributes: async (customerID, attributes) => {
        const query = setCustomerAttributesGQL(self.isPortalApi);
        const variables = {
            attributes,
            organizationID: self.currentOrganization?.id,
            customerID,
        };
        const request = {
            authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
            query,
            variables,
        };
        console.log('setCustomerAttributes', request);
        const response = await getGQLAPI().graphql(request);
        console.log('response', response);
        return response.data.setCustomerAttributes;
    },
    startCheckout: async (items, cancelUrl) => {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        console.log('current Customer', self.currentCustomer);
        // doing it this way so that multiple calls to this function
        // only produce one call to checkout
        const loadKey = `checkout:${items.toString()}`;
        let loadPromise = loadItemMap.get(loadKey);
        if (loadPromise) {
            console.log('Returning existing promise');
            return loadPromise;
        }
        // start the load and store the promise
        const doCheckout = async () => {
            console.log('**************** START CHECKOUT ******************');
            const startCheckoutAction = async (portalApi, data) => {
                const gql = startCheckoutGQL(portalApi);
                console.log('Calling checkout with', gql, data);
                const response = await getGQLAPI().graphql(graphqlOperation(gql, data));
                return response.data.startCheckout;
            };
            const resp = await startCheckoutAction(self.isPortalApi, {
                items,
                organizationID: self.currentOrganization?.id,
                portalID: self.currentPortal?.id,
                returnUrl: window.location.href,
                cancelUrl: `${window.location.origin}${cancelUrl}`,
            });
            let member;
            if (resp.statusCode === 200 || resp.statusCode === 201 || resp.statusCode === 202) {
                member = self.remoteItemToLocal('member', resp.member);
                await self.refreshCognito();
            }
            // } else if (resp.statusCode === 300) {
            //   member = self.getOrLoad('member', resp.member.id);
            //   schema.mapValuesFromGet('member', resp.member, member);
            // }
            console.log('Returning', { resp, member });
            loadItemMap.delete(loadKey);
            return { ...resp, member };
        };
        loadPromise = doCheckout();
        loadItemMap.set(loadKey, loadPromise);
        return loadPromise;
    },
    syncFiles: async (folderIds) => {
        try {
            if (!self.currentOrganization) {
                throw Error('currentOrganization cannot be empty');
            }
            const repositoryID = self.currentRepository?.id;
            const libraryID = self.currentPortal?.defaultLibrary?.id;
            const variables = {
                folderIds,
                organizationID: self.currentOrganization.id,
                repositoryID,
                libraryID,
            };
            const result = await syncDriveFilesAction(variables);
            if (result.statusCode === 200) {
                /* const assets = */ result.results?.map((asset) => (self.remoteItemToLocal('asset', asset)));
                return result;
            }
            throw new Error(result.message || 'Error syncing files');
        }
        catch (e) {
            console.error('Error syncing files:', e);
            throw e;
        }
    },
    getGoogleAccessToken: async (authorizationCode, redirectUri) => {
        try {
            if (!self.currentOrganization) {
                throw Error('currentOrganization cannot be empty');
            }
            const variables = {
                authorizationCode,
                redirectUri,
                organizationID: self.currentOrganization.id,
            };
            const result = await getGoogleAccessTokenAction(variables);
            if (result.statusCode === 200) {
                return result;
            }
            throw new Error(result.message || 'Error fetching Google Access Token');
        }
        catch (e) {
            console.error('Error fetching Google Access Token:', e);
            throw e;
        }
    },
    getGoogleSyncConfig: async () => {
        try {
            if (!self.currentOrganization) {
                throw Error('currentOrganization cannot be empty');
            }
            const variables = {
                organizationID: self.currentOrganization.id,
            };
            const result = await getGoogleSyncConfigAction(variables);
            if (result.statusCode === 200) {
                return result;
            }
            throw new Error(result.message || 'Error fetching Google Sync Config');
        }
        catch (e) {
            console.error('Error fetching Google Sync Config:', e);
            throw e;
        }
    },
    verifyCheckout: flow(function* verifyCheckoutGenerator(memberID) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        const resp = yield verifyCheckoutAction(self.isPortalApi, {
            organizationID: self.currentOrganization.id,
            portalID: self.currentPortal.id,
            memberID,
        });
        if (resp.statusCode === 200) {
            const member = self.remoteItemToLocal('member', resp.member);
            yield self.refreshCognito();
            return { ...resp, member };
        }
        throw Error(resp.message);
    }),
    createLeads: flow(function* createLeads(lead) {
        const request = {
            query: createLead,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                email: lead.email,
                firstName: lead.firstName,
                lastName: lead.lastName,
                company: lead.company,
                phoneNumber: lead.phoneNumber,
                emailVerified: true,
            },
        };
        const result = yield getGQLAPI().graphql(request);
        console.log('Pallavi create Lead', result);
        const response = result.data.createLead;
        if (response.statusCode !== 200) {
            throw Error(result.message);
        }
        const { cognitoLead } = response;
        const newLead = yield self.add('lead', {
            ...lead,
            id: `${self.currentOrganization?.cognitoUserPoolID}_${cognitoLead.Username}`,
            firstName: lead.firstName,
            lastName: lead.lastName,
            company: lead.company,
            phoneNumber: lead.phoneNumber,
        });
        return newLead;
    }),
    createTopic: flow(function* createTopic(topic) {
        const request = {
            query: createCognitoCustomer,
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                name: topic.name,
                description: topic.description,
                approvalRequired: topic.approvalRequired,
                oneOnOne: topic.oneOnOne,
            },
        };
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        const response = result.data.newTopi;
        if (response.statusCode !== 200) {
            throw Error(result.message);
        }
        const { cognitoCustomer } = response;
        const newTopic = yield self.add('discussionTopic', {
            ...topic,
            id: `${self.currentOrganization?.topicID}_${cognitoCustomer.Username}`,
        });
        return newTopic;
    }),
    createMeeting: flow(function* createMeeting(name) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        try {
            const request = {
                query: createPortalMeetingGQL(self.isPortalApi),
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                variables: {
                    organizationID: self.currentOrganization?.id,
                    portalID: self.currentPortal?.id,
                    libraries: [self.currentPortal?.defaultLibrary?.id],
                    name,
                },
            };
            console.log('createMeeting request', request);
            const result = yield getGQLAPI().graphql(request);
            console.log('createMeeting result', result);
            const meeting = self.remoteItemToLocal('meeting', result.data.createPortalMeeting);
            self.currentMeeting = meeting;
            return meeting;
        }
        catch (error) {
            console.error('Error creating meeting', error);
            throw error;
        }
    }),
    startMeeting: flow(function* startMeeting(meetingKey) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        try {
            const request = {
                query: startMeetingGQL(self.isPortalApi),
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                variables: {
                    organizationID: self.currentOrganization?.id,
                    meetingKey,
                },
            };
            console.log('startMeeting request', request);
            const result = yield getGQLAPI().graphql(request);
            console.log('startMeeting result', result);
            const meeting = self.remoteItemToLocal('meeting', result.data.startMeeting.Meeting);
            self.currentMeeting = meeting;
            return {
                currentMeeting: meeting,
                message: result.data.startMeeting.message,
            };
        }
        catch (error) {
            console.error('Error starting meeting', error);
            throw error;
        }
    }),
    joinMeeting: flow(function* joinMeeting(meetingKey) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        try {
            const request = {
                query: joinMeetingGQL(self.isPortalApi),
                authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
                variables: {
                    organizationID: self.currentOrganization?.id,
                    portalID: self.currentPortal?.id,
                    meetingKey,
                    attendeeName: self.currentCustomer?.name,
                },
            };
            console.log('request', request);
            const result = yield getGQLAPI().graphql(request);
            console.log('result', result);
            const joinMeetingResult = result?.data?.joinMeeting;
            if (joinMeetingResult?.statusCode !== '200') {
                throw new Error(result?.message || 'Error on join meeting.');
            }
            const meeting = self.remoteItemToLocal('meeting', joinMeetingResult.Meeting);
            const registration = self.remoteItemToLocal('meetingRegistration', joinMeetingResult.Registration);
            self.currentMeeting = meeting;
            self.currentRegistration = registration;
            self.currentAttendee = joinMeetingResult.CurrentAttendee;
            return {
                currentMeeting: meeting,
                currentAttendee: self.currentAttendee,
                currentRegistration: registration,
            };
        }
        catch (error) {
            console.error('Error joining meeting', error);
            return {
                error,
            };
        }
    }),
    endMeeting: flow(function* endMeeting(meetingKey) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        const request = {
            query: endMeetingGQL(self.isPortalApi),
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                portalID: self.currentPortal?.id,
                meetingKey,
            },
        };
        console.log('request', request);
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        const meeting = self.remoteItemToLocal('meeting', result.data.endMeeting.Meeting);
        const registration = self.remoteItemToLocal('meetingRegistration', result.data.endMeeting.Registration);
        return {
            meeting,
            registration,
        };
    }),
    startMeetingRecording: flow(function* startMeetingRecording(meetingKey) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        const request = {
            query: startMeetingRecordingGQL(self.isPortalApi),
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                portalID: self.currentPortal?.id,
                meetingKey,
            },
        };
        console.log('request', request);
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        const { Meeting: newMeeting, message, statusCode } = result.data.startMeetingRecording;
        let meeting;
        if (newMeeting) {
            meeting = self.remoteItemToLocal('meeting', newMeeting);
        }
        return {
            meeting,
            message,
            statusCode,
        };
    }),
    stopMeetingRecording: flow(function* stopMeetingRecording(meetingKey) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        const request = {
            query: stopMeetingRecordingGQL(self.isPortalApi),
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization?.id,
                portalID: self.currentPortal?.id,
                meetingKey,
            },
        };
        console.log('request', request);
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        const { Meeting: newMeeting, message, statusCode } = result.data.stopMeetingRecording;
        let meeting;
        if (newMeeting) {
            meeting = self.remoteItemToLocal('meeting', newMeeting);
        }
        return {
            meeting,
            message,
            statusCode,
        };
    }),
    getSignedCertificateUrl: flow(function* getUrl(certificateID) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        if (!self.currentPortal) {
            throw Error('currentPortal cannot be empty');
        }
        const request = {
            query: getSignedCertificateUrl(self.isPortalApi),
            authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
            variables: {
                ...(self.isPortalApi ? {} : { organizationID: self.currentOrganization?.id }),
                certificateID,
            },
        };
        console.log('request', request);
        const result = yield getGQLAPI().graphql(request);
        console.log('result', result);
        if (result.data.getSignedCertificateUrl.statusCode === 200) {
            return result.data.getSignedCertificateUrl.url;
        }
        throw Error('Error getting certificate url');
    }),
    previewCertificate: flow(function* previewCert(certificationID, fields, courseKey, programKey) {
        if (!self.currentOrganization) {
            throw Error('currentOrganization cannot be empty');
        }
        const request = {
            query: previewCertificate(self.isPortalApi),
            authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
            variables: {
                organizationID: self.currentOrganization.id,
                certificationID,
                fields,
                courseKey: courseKey || null,
                programKey: programKey || null,
            },
        };
        console.log('previewCertificate request', request);
        const result = yield getGQLAPI().graphql(request);
        console.log('previewCertificate result', result);
        if (result.data.previewCertificate.statusCode === 200) {
            return result.data.previewCertificate.url;
        }
        throw Error(result.data.previewCertificate.body || 'Error generating certificate preview');
    }),
    // createConversation: (snapshot:any) => {
    //   const data = {
    //     id: v4(),
    //     ...snapshot,
    //     createdAt: snapshot.createdAt ? new Date(snapshot.createdAt) : new Date(),
    //     updatedAt: snapshot.updatedAt ? new Date(snapshot.updatedAt) : new Date(),
    //     messages: snapshot?.messages?.map((message:any) => ({
    //       ...message,
    //       createdAt: new Date(message.createdAt),
    //       updatedAt: new Date(message.updatedAt),
    //     })) || [],
    //   };
    //   console.log('Creating conversation', data);
    //   const conversation = Conversation.create(data);
    //   self.currentConversation = conversation;
    //   return conversation;
    // },
    invokeAgent: async (prompt, sessionID, options, adminOptions) => {
        const startRequestTime = Date.now();
        console.log('invokeAgent', prompt, sessionID, options, adminOptions);
        const processResults = async (results) => {
            const batchKeys = results.itemReferences?.map((ref) => ({
                modelName: ref.type,
                key: ref[ref.type],
            }));
            console.log('batchKeys', batchKeys);
            const itemReferences = batchKeys
                ? await self.batchLoad(uniqBy(batchKeys, 'key'))
                : [];
            return {
                completion: results.completion,
                itemReferences,
            };
        };
        try {
            const result = await getGQLAPI().graphql({
                query: invokeAgentGQL(self.isPortalApi),
                variables: {
                    organizationID: self.currentOrganization?.id,
                    portalID: self.currentPortal?.id,
                    sessionID,
                    prompt,
                    options: JSON.stringify(options),
                    adminOptions: JSON.stringify(adminOptions),
                },
                authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
            });
            if (!result.data || !result.data.invokeAgent) {
                throw Error('no data returned');
            }
            const results = JSON.parse(result.data.invokeAgent);
            return await processResults(results);
        }
        catch (error) {
            console.error('Error invoking agent', error);
            if (error.errors[0].errorType === 'Lambda:ExecutionTimeoutException') {
                // if it is a timeout error, poll for a result
                console.log('Polling for result');
                // wait 10 seconds
                await new Promise((resolve) => {
                    setTimeout(resolve, 10000);
                });
                const result = await getGQLAPI().graphql({
                    query: getAgentSessionGQL(self.isPortalApi),
                    variables: {
                        organizationID: self.currentOrganization?.id,
                        portalID: self.currentPortal?.id,
                        sessionID,
                    },
                    authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
                });
                if (!result.data || !result.data.getAgentSession) {
                    throw Error('no data returned');
                }
                const sessionResults = JSON.parse(result.data.getAgentSession);
                const results = sessionResults[sessionResults.length - 1];
                if (results.requestTime > startRequestTime) {
                    return await processResults(results);
                }
                return 'error';
            }
            return {
                error,
            };
        }
    },
    getAllAISessions: flow(function* getAllAISessions() {
        try {
            if (!self.currentOrganization) {
                throw new Error('currentOrganization cannot be empty');
            }
            const variables = {
                organizationID: self.currentOrganization.id,
            };
            const result = yield getAllAISessionsAction(variables);
            if (result?.statusCode === 200) {
                return result.sessions;
            }
            throw new Error(result.message || 'Error fetching AI sessions');
        }
        catch (error) {
            console.error('Error fetching AI sessions:', error);
            throw error;
        }
    }),
    getAISession: flow(function* getAISession(sessionId) {
        try {
            if (!self.currentOrganization) {
                throw new Error('currentOrganization cannot be empty');
            }
            const variables = {
                organizationID: self.currentOrganization.id,
                sessionId,
            };
            const result = yield getAISessionAction(variables);
            if (result?.statusCode === 200) {
                return result.session;
            }
            throw new Error(result?.message || 'Error fetching AI session');
        }
        catch (error) {
            console.error('Error fetching AI session:', error);
            throw error;
        }
    }),
    runQuery: flow(function* getQueryResults(queryName, args) {
        try {
            if (!self.currentOrganization) {
                throw new Error('currentOrganization cannot be empty');
            }
            const variables = {
                queryName,
                organizationID: self.currentOrganization.id,
            };
            if (args) {
                variables.args = JSON.stringify(args);
            }
            const result = yield runQueryAction(variables);
            if (result?.statusCode === 200 && result.results) {
                console.log('Athena query results:', result.results);
                return result.results;
            }
            throw new Error(result.message || 'Error executing Athena query');
        }
        catch (error) {
            console.error('Error executing Athena query:', error);
            throw error;
        }
    }),
    // appendCustomerContext(data:any) {
    //   console.log('appendCustomerContext', data);
    //   if (!self.customerContext) {
    //     // const pageHistory = PageHistory.create();
    //     self.customerContext = CustomerContext.create({
    //       currentPage: data.details?.currentPage,
    //     });
    //   } else {
    //     self.customerContext.currentPage = data.details?.currentPage;
    //   }
    // },
}));
//# sourceMappingURL=RootModel.js.map